/*
vue-i18n (v8.x) guide
https://kazupon.github.io/vue-i18n/guide/formatting.html
*/

import { formattedDate } from './dateTimeFormatter.ts';

const deDE = {
    booking_status_labels: {
        cancelled: 'Storniert',
        confirmed: 'Bestätigt',
        failed: 'Fehlgeschlagen',
        pending: 'Ausstehend',
        waiting_for_payment: 'Warten auf Zahlung',
    },
    ticket_purchase_status_labels: {
        in_process: 'In Bearbeitung',
        payment_failed: 'Zahlung fehlgeschlagen',
        payment_processing: 'Zahlung wird verarbeitet',
        unspecified: 'Nicht angegeben',
    },
    countWithUnit: {
        days: '{count} Tag | {count} Tage',
        followers: 'kein Follower | {count} Follower | {count} Follower',
        hours: '{count} Stunde | {count} Stunden',
        months: '{count} Monat | {count} Monate',
        weeks: '{count} Woche | {count} Wochen',
        years: '{count} Jahr | {count} Jahre',
    },
    coWeekdaySelector: {
        ariaLabel: 'Wochentage auswählen',
        weekdays: {
            fridayShort: 'Fr',
            mondayShort: 'Mo',
            saturdayShort: 'Sa',
            sundayShort: 'So',
            thursdayShort: 'Do',
            tuesdayShort: 'Di',
            wednesdayShort: 'Mi',
        },
    },
    coUserInvite: {
        csvClear: 'CSV löschen',
        csvErrorEmpty: 'Die CSV-Datei ist leer.',
        csvErrorInvalidType: 'Bitte lade eine gültige CSV-Datei hoch.',
        csvErrorNoEmails: 'In der CSV-Datei wurden keine gültigen E-Mail-Adressen gefunden.',
        csvErrorRead: 'Die CSV-Datei konnte nicht gelesen werden.',
        csvPreviewEmailColumn: '@:labels.email',
        csvPreviewNumberColumn: '#',
        csvPreviewTitle: 'Vorschau ({count} Einladung) | Vorschau ({count} Einladungen)',
        csvSelectButton: 'Datei auswählen',
        csvSelectedFile: 'Ausgewählte Datei: {name}',
        csvTemplateDownload: 'Beispiel-CSV',
        csvTemplateHelp:
            'Brauchst du eine Vorlage? Lade die Beispiel-CSV herunter und ersetze die E-Mail-Adressen durch deine eigenen: {0}',
        csvUploadLabel: 'E-Mail Einladungsliste als CSV hochladen',
        inviteGeneralError: 'Einladungslink konnte nicht gesendet werden',
        invitePartialError: 'Einige Einladungen konnten nicht gesendet werden',
        inviteperemail: 'Per E-Mail einladen',
        inviteResultFailure: 'Einladung konnte nicht gesendet werden',
        inviteResultStatus: 'Status',
        inviteResultsTitle: 'Einladungsergebnisse',
        inviteResultSuccess: 'Einladung gesendet',
        inviteSuccessMessage: 'Einladungslink gesendet',
        manualEntryHelp: 'Gib eine gültige E-Mail-Adresse ein und drücke Enter.',
        modalTitle: '@:labels.invitemember',
        progressInfo:
            'Einladungen werden nacheinander versendet. Bitte lasse dieses Fenster geöffnet, bis der Vorgang abgeschlossen ist.',
        progressStatus: '{current} von {total} Einladungen gesendet',
        seeInviteList: 'Einladungsliste ansehen',
        sendButtonLabel: 'Einladung senden | Einladungen senden',
    },
    communityFeed: {
        postNudge: {
            dismissed: 'Beitragsvorschläge werden 14 Tage lang ausgeblendet.',
            nextSuggestion: 'Nächster Vorschlag',
            suggestions: {
                ama: {
                    label: 'AMA veranstalten',
                    title: 'Lade zu einer Fragerunde ein',
                    message: 'Erstelle ein Event, bei dem deine Community Fragen stellen und mitdiskutieren kann.',
                },
                challenge: {
                    label: 'Challenge starten',
                    title: 'Schaffe einen Grund zurückzukommen',
                    message: 'Starte eine einfache Aktion, an der deine Community regelmäßig teilnehmen kann.',
                },
                guide: {
                    label: 'Guide oder Vorlage teilen',
                    title: 'Teile etwas Nützliches',
                    message:
                        'Veröffentliche eine praktische Vorlage oder Checkliste, auf die Mitglieder immer wieder zurückgreifen.',
                },
                meme: {
                    label: 'Meme teilen',
                    title: 'Teile einen Insider',
                    message:
                        'Ein passendes Meme oder ein Insider sorgt für Reaktionen und stärkt das Gemeinschaftsgefühl.',
                },
                poll: {
                    label: 'Umfrage erstellen',
                    title: 'Mach Mitmachen einfach',
                    message: 'Stelle eine kurze Umfrage oder eine einfache Entweder-oder-Frage.',
                },
                question: {
                    label: 'Frage stellen',
                    title: 'Starte ein Gespräch',
                    message: 'Stelle eine konkrete Frage, die zu hilfreichen Antworten und Austausch einlädt.',
                },
                spotlight: {
                    label: 'Mitglied hervorheben',
                    title: 'Stelle jemanden ins Rampenlicht',
                    message: 'Feiere einen Meilenstein oder einen wertvollen Beitrag aus deiner Community.',
                },
            },
        },
    },
    datetime: ({ named }) =>
        formattedDate(named('date'), named('format'), named('locale') ? named('locale') : 'de', named('customFormat')),
    deleteChannelInfo:
        'Sobald du einen Kanal löschst, gibt es kein Zurück. Bitte verschiebe alle Seiten, die diesen Kanal verwenden, in einen anderen Kanal.',
    errors: {
        tooManyRequests: 'Zu viele Anfragen von deiner IP-Adresse. Bitte warte einen Moment und versuche es erneut.',
        accessManagement: {
            // ErrAccessManagementSystemIsNotAvailable is used when the access management system is not available/offline.
            ErrAccessManagementSystemIsNotAvailable:
                'Zugangssystem ist nicht verfügbar/offline. Stelle sicher, dass das Zugangssystem online und verfügbar ist, und versuche es erneut.',
            // ErrAccessManagementSystemIsNotConfigured is used when the access management system is not configured.
            ErrAccessManagementSystemIsNotConfigured:
                'Zugangssystem ist nicht konfiguriert, bitte kontaktiere den Support.',
            // ErrAccessManagementSystemUnexpectedError is used when an unexpected error occurs in the access management system.
            ErrAccessManagementSystemUnexpectedError:
                'Unerwarteter Fehler im Zugangssystem. Stelle sicher, dass das Zugangssystem online und verfügbar ist, und versuche es erneut.',
            // ErrEmailIsRequired is used when email is required but not provided.
            ErrEmailIsRequired: 'Ungültige Anfrage: E-Mail ist erforderlich, bitte kontaktiere den Support.',
            // ErrFailedToAssignKeyToUser is used when assigning a key to a user fails and should try another key.
            ErrFailedToAssignKeyToUser:
                'Fehler beim Zuweisen des Schlüssels an den Benutzer, dies bedeutet wahrscheinlich, dass der Schlüssel ungültig oder bereits in Verwendung ist. Bitte versuche einen anderen Schlüssel.',
            ErrFailedToAssignPolicyToUser:
                'Fehler beim Zuweisen der Richtlinie an den Benutzer, dies bedeutet wahrscheinlich, dass die Richtlinie falsch konfiguriert oder nicht mit coapp synchronisiert ist. Bitte synchronisiere die Richtlinien in coapp und versuche es erneut.',
            // ErrFailedToCreateUser is used when user creation in access management system fails.
            ErrFailedToCreateUser:
                'Fehler beim Erstellen des Benutzers im Zugangssverwaltungssystem. Stelle sicher, dass das Zugangssystem online und verfügbar ist, und versuche es erneut.',
            // ErrFailedToCreateUserInDB is used when user creation in the database fails.
            ErrFailedToCreateUserInDB:
                'Fehler beim Erstellen des Benutzers in der Datenbank, bitte kontaktiere den Support.',
            // ErrFailedToDeleteUser is used when user deletion in access management system fails.
            ErrFailedToDeleteUser:
                'Fehler beim Löschen des Benutzers im Zugangssverwaltungssystem. Stelle sicher, dass das Zugangssystem online und verfügbar ist, und versuche es erneut.',
            // ErrFailedToDeleteUserInDB is used when user deletion in the database fails.
            ErrFailedToDeleteUserInDB:
                'Fehler beim Löschen des Benutzers in der Datenbank. Stelle sicher, dass das Zugangssystem online und verfügbar ist, und versuche es erneut.',
            // ErrFailedToFetchUser is used when fetching user from access management system fails.
            ErrFailedToFetchUser:
                'Fehler beim Abrufen des Benutzers aus dem Zugangssverwaltungssystem, bitte kontaktiere den Support.',
            // ErrFailedToFetchUserFromDB is used when fetching user from the database fails
            ErrFailedToFetchUserFromDB:
                'Fehler beim Abrufen des Benutzers aus der Datenbank, bitte kontaktiere den Support.',
            // ErrFailedToUpdateUser is used when user update in access management system fails.
            ErrFailedToUpdateUser:
                'Fehler beim Aktualisieren des Benutzers im Zugangssverwaltungssystem. Stelle sicher, dass das Zugangssystem online und verfügbar ist, und versuche es erneut.',
            // ErrFailedToUpdateUserInDB is used when user update in the database fails.
            ErrFailedToUpdateUserInDB:
                'Fehler beim Aktualisieren des Benutzers in der Datenbank, bitte kontaktiere den Support.',
            // ErrIdentityIDIsRequired returned if client tries to update, delete or fetch user without providing access identity ID.
            ErrIdentityIDIsRequired:
                'Ungültige Anfrage: Identitäts-ID ist erforderlich, bitte kontaktiere den Support.',
            // ErrSpaceIDIsRequired is used when space ID is required but not provided.
            ErrSpaceIDIsRequired: 'Ungültige Anfrage: Space ID ist erforderlich, bitte kontaktiere den Support.',
            ErrUnspecified:
                'Ein nicht spezifizierter Fehler ist im Zugangssystem aufgetreten, bitte kontaktiere den Support.',
            // ErrUserIsNotFound is used when user is not found in access management system.
            ErrUserIsNotFound: 'Benutzer nicht im Zugangssverwaltungssystem gefunden',
            // ErrUserIsNotFoundInDB is used when user is not found in the database.
            ErrUserIsNotFoundInDB: 'Benutzer nicht in der Datenbank gefunden',
        },
        atLeastOneMethod: 'Mindestens eine Zahlungsmethode muss aktiviert sein.',
        failedGeneric: 'Es ist ein Fehler aufgetreten.',
        failedToSaveChanges: 'Änderungen konnten nicht gespeichert werden.',
        fileTooLarge: 'Datei ist zu groß. Maximale Dateigröße ist {size} MB.',
        inputinvalid: 'Ungültige Eingabe',
        inputrequired: 'Pflichtfeld',
        invalid: '@:labels.invalid',
        invalidLoginCredentials: 'Ungültige Anmeldedaten',
        localstorageDisabled:
            'LocalStorage ist deaktiviert. Ohne diese Browserfunktion kann coapp nicht verwendet werden. Bitte aktiviere LocalStorage in deinen Browsereinstellungen, um fortzufahren.',
        mustBeValidEmail: 'Muss eine gültige E-Mail-Adresse sein',
        noPaymentMethods: 'Du hast keine Zahlungsmethoden. Bitte erstelle eine, bevor du ein Abo abschließt.',
        uploadFailed: 'Upload fehlgeschlagen. Bitte versuche es erneut.',
        usernotfound: 'Benutzer nicht gefunden',
    },
    // Optionen für die Teilnahmeart während der Event-Erstellung.
    eventParticipationOptions: {
        external: {
            description:
                'Lass Mitglieder hier zusagen und zeige zusätzlich einen Link zu einer externen Anmeldeseite. Es wird keine Preisangabe angezeigt.',
            title: 'Externer Registrierungslink',
        },
        internal: {
            description:
                'Nutze Tickets, um die Teilnehmerzahlen zu regulieren, verschiedene Teilnahmekategorien zu erstellen und Einnahmen zu generieren. Kostenpflichtige Tickets können nur von Admins eingerichtet werden. Wenn du deine Veranstaltung mit Tickets erstellst, kannst du später nicht mehr in einen anderen Modus wechseln. Du kannst jederzeit Tickets bearbeiten oder hinzufügen.',
            nonAdminNotice:
                'Du kannst kostenlose Tickets erstellen. Wenn diese Veranstaltung kostenpflichtige Tickets braucht, wende dich an einen Admin.',
            title: 'Tickets anbieten',
        },
        rsvp: {
            description:
                'Lass Mitglieder ohne Ticket zusagen. Gäste können öffentliche Veranstaltungen sehen, benötigen zur Anmeldung aber ein Ticket.',
            guestRegistrationInfo:
                'Nicht-Mitglieder können sich für diese Veranstaltung aktuell nicht anmelden, da sie keine Tickets hat.',
            guestRegistrationNotice: {
                title: 'Wer kann sich anmelden?',
                options: {
                    rsvp: {
                        title: 'Mit „Teilnahmezusage“',
                        description: 'Nur registrierte Community-Mitglieder können sich anmelden.',
                    },
                    tickets: {
                        title: 'Mit „Tickets anbieten“',
                        description:
                            'Auch Gäste außerhalb deiner Community können sich anmelden. Aktiviere dazu „Tickets anbieten“ und erstelle mindestens ein Ticket. Das kann auch kostenlos sein.',
                    },
                },
            },
            title: 'Teilnahmezusage',
        },
    },
    // Event-Formate während der Event-Erstellung.
    eventFormatOptions: {
        hybrid: {
            description: 'Teile sowohl einen physischen Ort als auch einen Link für Teilnehmende.',
            title: 'Hybride Veranstaltung',
        },
        online: {
            description:
                'Führe die Veranstaltung online durch und zeige einen Link zur Videokonferenz oder zum Livestream. Der Link ist nur für Teilnehmende sichtbar.',
            title: 'Online-Veranstaltung',
        },
        physical: {
            description:
                'Die Veranstaltung findet an einem physischen Ort statt. Zeige den Veranstaltungsort oder Treffpunkt.',
            title: 'Präsenzveranstaltung',
        },
    },
    isLastChannelWarning:
        'Du bist dabei, den letzten Kanal zu löschen. Seiten können nur erstellt werden, wenn Kanäle verfügbar sind, in denen der Benutzer das Recht hat, eine Seite zu erstellen. Das Löschen dieses Kanals macht es unmöglich, neue Seiten zu erstellen, bis du einen neuen Kanal erstellst.',
    labels: {
        // Headings and controls for the legal and compliance settings.
        legalandcompliance: 'Rechtliches und Compliance',
        purchaseCancellationPolicy: 'Widerrufsbelehrung',
        editPurchaseCancellationPolicy: 'Widerrufsbelehrung bearbeiten',
        enablePurchaseCancellationPolicy: 'Widerrufsbelehrung aktivieren',
        purchasewithdrawalform: 'Widerrufsformular',
        editAccessibilityPolicy: 'Barrierefreiheitserklärung bearbeiten',
        editImprintAndPrivacyPolicy: 'Impressum und Datenschutzerklärung bearbeiten',
        editTermsAndConditions: 'Nutzungsbedingungen bearbeiten',
        enableAccessibilityPolicy: 'Barrierefreiheitserklärung aktivieren',
        enableImprintAndPrivacyPolicy: 'Impressum und Datenschutzerklärung aktivieren',
        enablePurchaseWithdrawalForm: 'Widerrufsformular aktivieren',
        enableTermsAndConditions: 'Nutzungsbedingungen aktivieren',
        about: 'Über',
        aboutme: 'Über mich',
        applePodcastsAudioPlayer: 'Apple-Podcasts-Audioplayer',
        audioSection: 'Podcast-/Audioabschnitt',
        audioUrl: 'Podcast- oder Audio-URL',
        accept: 'Akzeptieren',
        acceptprivacy: 'Ich habe die {0} zur Kenntnis genommen',
        acceptprivacyandterms: 'Ich habe die {0} zur Kenntnis genommen und akzeptiere die {1}.',
        acceptterms: 'Ich akzeptiere die {0}',
        access: 'Zugang',
        accessibilitypolicy: 'Barrierefreiheitserklärung',
        accesskey: 'Zugriffsschlüssel',
        accessmanagement: 'Zutrittsverwaltung',
        accesspolicy: 'Zugriffsrichtlinie',
        account: 'Konto',
        accountcreated: 'Konto erstellt',
        accountsettings: 'Kontoeinstellungen',
        actions: 'Aktionen',
        activatedeactivate: 'Aktivieren / deaktivieren',
        activatepayments: 'Zahlungen aktivieren',
        active: 'Aktiv',
        activemembers: 'Aktive Mitglieder',
        activity: 'Aktivität',
        accountingcode: 'Buchungscode',
        add: 'Hinzufügen',
        addaddress: 'Neue Adresse hinzufügen',
        addblockingrule: 'Blockierregel hinzufügen',
        addbookingresourcegroup: 'Buchungsressourcengruppe hinzufügen',
        addcalendaraccount: 'Kalenderkonto hinzufügen',
        addemail: 'E-Mail hinzufügen',
        addavailability: 'Verfügbarkeit hinzufügen',
        addkey: 'Schlüssel hinzufügen',
        addoverride: 'Überschreibung hinzufügen',
        addplanupgrade: 'Tarif-Upgrade hinzufügen',
        addresource: 'Ressource hinzufügen',
        address: 'Adresse',
        addressadditionalinformation: 'Adresszusatz',
        addrow: 'Zeile hinzufügen',
        addrule: 'Regel hinzufügen',
        addsection: 'Abschnitt hinzufügen',
        addtogoogle: 'Zu Google hinzufügen',
        addtooutlook: 'Zu Outlook hinzufügen',
        addupgrade: 'Upgrade hinzufügen',
        addvideoconferencelink: 'Videokonferenz-Link',
        unlock: 'Freischalten',
        admin: 'Admin',
        adminadd: 'Administrator hinzufügen',
        administrators: 'Administratoren',
        // Link to administrator management from legal settings.
        viewAdministrators: 'Administratoren anzeigen',
        adminremove: 'Administrator entfernen',
        afterbookingbehavior: 'Verhalten nach der Buchung',
        afterbookingbuttontitle: 'Button-Titel nach der Buchung (optional)',
        afterbookingbuttonurl: 'Button-URL nach der Buchung (optional)',
        afterbookinginfoheading: 'Informationen für Nutzer nach der Buchungsbestätigung',
        afterbookingtext: 'Text nach der Buchung (optional)',
        afterbookingtitle: 'Titel nach der Buchung (optional)',
        agreements: 'Vereinbarungen',
        all: 'Alle',
        allowedvisibilities: 'Erlaubte Sichtbarkeiten',
        allresources: 'Alle Ressourcen',
        amount: 'Betrag',
        and: 'und',
        announcement: 'Ankündigung',
        announcementmake: 'Zur @:labels.announcement machen',
        announcementsentby: '@:labels.announcement gesendet von {name}',
        apikey: 'API-Schlüssel',
        apikeycreated: 'API-Schlüssel erstellt',
        apply: 'Anwenden',
        applynewcolors: 'Neue Farben anwenden',
        archive: 'Archivieren',
        archived: 'Archiviert',
        aspectratio: 'Seitenverhältnis',
        aspectratiolandscape: 'Querformat',
        aspectratioportrait: 'Hochformat',
        aspectratiosquare: 'Quadrat',
        assignaplan: 'Tarif zuweisen',
        assignkey: 'Schlüssel zuweisen',
        assignsubscription: 'Abonnement zuweisen',
        attend: 'Teilnehmen',
        attendees: 'Teilnehmende',
        authentication: 'Authentifizierung',
        authenticationtype: 'Authentifizierungstyp',
        author: 'Autor',
        authtoken: 'Auth-Token',
        authurl: 'Auth-URL',
        availability: 'Verfügbarkeit',
        availablebookingupgrades: 'Verfügbare Buchungs-Upgrades',
        availableforbooking: 'Für Buchung verfügbar',
        awaynotice: 'Abwesenheitsnotiz',
        back: 'Zurück',
        backtologin: 'Zurück zum Login',
        backfill: 'Backfill',
        bankcode: 'Bankleitzahl',
        baseurl: 'Basis-URL',
        bestoptions: 'Beste Optionen',
        billing: 'Abrechnung',
        billingadditionalinfo: 'Abrechnung: Zusatzinfo',
        billingaddress: 'Rechnungsadresse',
        billingaddressedit: '@:labels.billingaddress @.lower:labels.edit',
        billingadmin: '@:labels.owner',
        billingcity: 'Abrechnung: Stadt',
        billingcompany: 'Abrechnung: Firma',
        billingcountry: 'Abrechnung: Land',
        billingcycle: 'Abrechnungszyklus',
        billingcycleend: 'Ende des Abrechnungszyklus',
        billingcyclenotavailable: 'Kein Abrechnungszyklus',
        billingcyclestart: 'Beginn des Abrechnungszyklus',
        billingAccess: 'Zugriff auf Abrechnung',
        billingmanager: 'Billing-Manager',
        billingphone: 'Abrechnung: Telefon',
        billingpostalcode: 'Abrechnung: Postleitzahl',
        billingstreet: 'Abrechnung: Strasse',
        billingvatid: 'Abrechnung: USt-IdNr.',
        birthdate: 'Geboren am',
        block: 'Blockierung',
        blockingrules: 'Blockierregeln',
        book: 'Buchen',
        bookedasadmin: 'Als Admin gebucht',
        booking: 'Buchung',
        'booking-checkout': {
            bookingheader: '{resource} buchen',
            calendar: 'Buchung in deinem Kalender speichern',
            cancellationpolicy:
                'Diese Buchung kann bis zu ihrer Anfangszeit kostenlos storniert werden. Danach ist diese Buchung nicht mehr erstattungsfähig. | Diese Buchung kann bis {count} Stunden vor ihrer Anfangszeit kostenlos storniert werden. Danach ist diese Buchung nicht mehr erstattungsfähig.',
            couponnotneded: 'Die Buchung ist für dich bereits kostenlos, der Gutschein wird also nicht angewendet.',
            error: 'Leider ist bei der Buchung etwas schiefgegangen.',
            isexhausted: 'Die gewählte Zeit ist nicht buchbar.',
            lockavailabilitynotice:
                'Diese Ressource ist durch ein Schloss gesichert. Das Schloss kannst du eigenständig in der Detailansicht dieser Buchung unter "Meine Buchungen" ab {available_from} bis {available_until} öffnen.',
            processing: 'Deine Buchung wird verarbeitet.',
            seebookings: 'Meine Buchungen ansehen',
            success: 'Deine Buchung war erfolgreich',
            toolong:
                'Diese Ressource erlaubt eine maximale Buchungsdauer von {0} Minuten. Die Endzeit wurde entsprechend angepasst.',
            tooshort:
                'Diese Ressource erlaubt eine minimale Buchungsdauer von {0} Minuten. Die Endzeit wurde entsprechend angepasst.',
        },
        bookingBy: 'Erstellt von',
        bookingcalendar: 'Buchungskalender',
        bookingcancel: '@:labels.booking stornieren',
        bookingcategories: 'Buchungskategorien',
        bookingconditions: 'Buchungskonditionen',
        bookingconfirm: 'Buchen',
        bookingconfirmationemailcontent: 'Buchungsbestätigung E-Mail-Inhalt',
        bookingconfirmpaid: 'Kostenpflichtig buchen',
        bookingfor: 'Buchung für',
        bookingFor: 'Buchung für',
        bookingInterval: 'Buchungsintervall',
        bookinggranularity: 'Buchungsgranularität',
        bookinggranularity15minutes: '15 Minuten',
        bookinggranularity30minutes: '30 Minuten',
        bookinggranularityday: 'Tag',
        bookinggranularityhour: 'Stunde',
        bookingOverridePrice: 'Preis überschreiben',
        bookingresource: 'Buchungsressource',
        bookingresourcecreate: 'Buchungsressource erstellen',
        bookingresourceedit: 'Buchungsressource bearbeiten',
        bookingresources: 'Buchungsressourcen',
        allowstartedtimeslotbooking: 'Buchung nach Startzeit erlauben',
        bookings: 'Buchungen',
        bookingshistory: 'Buchungshistorie',
        bookingslot: 'Buchungsslot',
        bookingsmy: 'Meine Buchungen',
        bookingsother: 'Andere Buchungen',
        bookingstart: 'Buchungsstart',
        bookingsupcoming: 'Kommende Buchungen',
        bookingTitle: 'Buchungstitel',
        bookingupgrade: 'Buchungs-Upgrade',
        bookwholeslot: 'Gesamten Slot buchen',
        builtindomain: 'Integrierte Domain',
        bulkcreate: 'Bulk erstellen',
        bulkmembercreate: 'Mitglieder in Bulk erstellen',
        buy: 'Kaufen',
        buyhere: 'Hier kaufen',
        buynow: 'Jetzt kaufen',
        buyPass: {
            expireDate: 'Ablaufdatum',
            maxHoursPerUse: 'Max. Stunden pro Nutzung',
            messageOptional: 'Nachricht (optional)',
            numberOfTimesToUse: 'Anzahl der Nutzungen',
            overridePrice: 'Preis überschreiben',
            paymentDueToday: 'Zahlung heute fällig',
            payWith: 'Bezahlen mit',
            priceIncludesTax: 'Preis enthält {percentage}% {name} MwSt.',
            setNewPriceOptional: 'Neuen Preis festlegen (optional)',
        },
        by: 'Von',
        byme: '@:labels.mine',
        calendar: 'Kalender',
        calendarsynchronization: 'Kalendersynchronisierung',
        cancel: 'Abbrechen',
        canceled: 'Storniert',
        cancellationnotice: 'Kündigungsfrist',
        cancellationnoticemessage: 'Stornierung ist bis {days} Tage vor der Veranstaltung möglich.',
        cancellationperiod: 'Stornierungszeitraum',
        cancellationpolicy: 'Stornierungsbedingungen',
        cancellationpolicyindays: 'Stornierungsbedingungen (in Tagen)',
        cancelling: 'Wird abgebrochen ...',
        cancelmembership: 'Mitgliedschaft kündigen',
        cancelupcomingplan: 'Kommenden Tarif kündigen',
        cancelupcomingsubscription: 'Kommendes Abonnement kündigen',
        capacity: 'Kapazität',
        cardpayments: 'Kartenzahlungen',
        category: 'Kategorie',
        cf: {
            addCustomField: 'Benutzerdefiniertes Feld hinzufügen',
            dataType: 'Feldtyp',
            datatypeboolean: 'Ja/Nein (Boolean)',
            datatypedate: 'Datum',
            datatypelink: 'Link',
            datatypemultiselect: 'Mehrfachauswahl',
            datatypenumber: 'Zahl',
            datatypeselect: 'Auswahl',
            datatypetext: 'Text',
            editability: 'Wer dieses Feld bearbeiten kann',
            editabilityadmin: 'Admin',
            editabilityowner: 'Mitglied (selbst)',
            editCustomField: 'Benutzerdefiniertes Feld bearbeiten',
            identifier: 'Interner Feldbezeichner (nicht sichtbar)',
            isusedformatchingtooltip: 'Diese Angaben werden verwendet, um Profile miteinander zu matchen.',
            label: 'Name',
            optionsValues: 'Optionswerte',
            optionsValuesHint: 'Kommagetrennte Werte für Auswahl/Mehrfachauswahl',
            requestedOnSignUp: 'Beim Anmelden abfragen',
            requestedonsignupnone: 'Keine',
            requestedonsignupoptional: 'Optional',
            requestedonsignuprequired: 'Pflichtfeld',
            sortOrder: 'Position',
            useformatching: 'Für Matching verwenden',
            useformatchingtooltip: 'Verwende die Daten in diesem Feld, um deine Mitglieder miteinander zu verbinden.',
            visibility: 'Wer dieses Feld sehen kann',
            visibilityadmin: 'Admin',
            visibilitycommunity: 'Community',
            visibilityLevelsFromAdminPOV: {
                admin: 'Nur Admins',
                community: 'Alle Mitglieder',
                owner: 'Admins und Mitglied (selbst)',
            },
            visibilityLevelsFromUserPOV: {
                admin: '',
                community: 'Alle Mitglieder',
                owner: 'Nur Admins',
            },
            visibilityowner: 'Mitglied (selbst)',
            visibilitypublic: 'Öffentlich',
        },
        change: 'Ändern',
        changehistory: 'Änderungsverlauf',
        changes: 'Änderungen',
        changessaved: '@:messages.changessaved',
        changestatus: 'Status ändern',
        channel: 'Kanal',
        channeladd: '@:labels.channel @.lower:labels.add',
        channelEditTitle: 'Kanal bearbeiten',
        channelname: 'Kanalname',
        channelnew: '@:labels.channel @.lower:labels.create',
        channelPermissions: {
            addPages: 'Wer darf Seiten zu diesem Kanal hinzufügen',
            admins: 'Administratoren',
            adminsOnly: 'Nur Administrator:innen',
            placeholder: 'Kanalname',
        },
        channels: 'Kanäle',
        channelsandpages: 'Kanäle & Seiten',
        channelspages: 'Kanäle & Seiten',
        chargeautomatically: 'Automatisch abbuchen',
        chat: 'Chat',
        chatnew: 'Neuer @:labels.chat',
        chatuserhasleft: 'hat den Chat verlassen',
        checkins: 'Check-ins',
        checkintime: 'Check-in-Zeit',
        checkout: 'Zur Kasse',
        checkoutday: 'Check-out-Tag',
        checkoutsessionid: 'Checkout-Session-ID',
        checkouttime: 'Check-out-Zeit',
        checkouturl: 'Checkout-URL',
        choose: 'Auswählen',
        choosefile: 'Datei auswählen',
        chooseplan: 'Tarif auswählen',
        city: 'Stadt',
        clear: 'Zurücksetzen',
        clearall: 'Alles zurücksetzen',
        clearallfilters: 'Alle Filter zurücksetzen',
        clientid: 'Client-ID',
        confirmWithdrawal: 'Widerruf bestätigen',
        clientsecret: 'Client-Secret',
        close: 'Schließen',
        closed: 'Geschlossen',
        closedforbookings: 'Für Buchungen geschlossen',
        coappmy: 'Mein coapp',
        coappsupport: 'coapp Support',
        coBookingCalendar: {
            bookingCalendar: 'Buchungskalender',
            busy: 'Nicht verfügbar',
        },
        codeSection: 'Codeabschnitt',
        collectionmethod: 'Zahlungsmethode',
        color: 'Farbe',
        colorbackground: 'Hintergrundfarbe',
        colorforeground: 'Vordergrundfarbe',
        comment: 'Kommentar',
        commentdelete: '@:labels.comment @.lower:labels.delete',
        comments: 'Kommentare',
        community: 'Community',
        communityrecentlyseen: 'Zuletzt online',
        company: 'Firma',
        complete: 'Erledigen',
        completeSetup: 'Einrichtung abschließen',
        configuration: 'Einstellungen',
        confirm: 'Bestätigen',
        confirmationcode: 'Bestätigungscode',
        congratulations: 'Herzlichen Glückwunsch',
        connect: 'Verbinden',
        connected: 'Verbunden',
        connectnukiaccount: 'Mit dem NUKI-Webkonto verbinden',
        connecttostripe: 'Mit Stripe verbinden',
        contact: 'Kontakt',
        contactEmail: 'Kontakt-E-Mail',
        contactcoappsupport: 'bitte kontaktiere das coapp Support-Team',
        contactsaleslink: 'Kontaktiere unser Vertriebsteam',
        contactstripesupport: 'Stripe-Support kontaktieren',
        contactsupport: 'Support kontaktieren',
        continue: 'Weiter',
        contractWithdrawal: 'Vertrag widerrufen',
        contributers: 'Mitwirkende',
        contributersadd: 'Mitwirkende hinzufügen',
        contributor: 'Mitwirkende:r',
        cookies: '🍪 Cookies',
        copy: 'Kopieren',
        copyshareablelink: 'Teilbaren Link kopieren',
        copySuffix: 'Kopie',
        country: 'Land',
        couponcode: 'Rabatt-Code',
        coupons: 'Rabatt-Codes',
        create: 'Erstellen',
        createapikey: 'API-Schlüssel erstellen',
        created: 'Erstellt',
        createdat: 'Erstellt am',
        createdon: '@:labels.created am',
        createfirstplan: 'Ersten Tarif erstellen',
        createfirsttaxrate: 'Ersten Steuersatz erstellen',
        createfirstupgrade: 'Erstes Upgrade erstellen',
        createnewevent: 'Neues Event erstellen',
        createneweventtype: 'Neuen Event-Typ erstellen',
        createnewtype: 'Neuen Typ erstellen',
        createnow: 'Jetzt erstellen',
        createplan: 'Tarif erstellen',
        creator: 'Ersteller:in',
        credit: 'Guthaben',
        credits: 'Guthaben',
        creditsafterbooking: 'In diesem Abrechnungszyklus nach dieser Buchung verbleibendes Guthaben',
        creditsbeforebooking: 'Guthaben, das in diesem Abrechnungszyklus vor dieser Buchung verfügbar ist',
        creditsrenew: 'Info nächster Abrechnungszyklus beginnt am {datetime}',
        creditssummary: 'Guthabenübersicht',
        creditstotal: 'Gesamtguthaben in diesem Abrechnungszeitraum',
        creditsused: 'Verwendetes Guthaben',
        csvpreview: 'CSV-Vorschau ({count})',
        currency: 'Währung',
        currentplan: 'Aktueller Tarif',
        currentplanstart: 'Aktueller Tarif gestartet am',
        currentplanend: 'Aktueller Tarif endet am',
        custom: 'Benutzerdefiniert',
        customernumbersettings: 'Kundennummer-Einstellungen',
        customersettings: 'Kundeneinstellungen',
        customfields: 'Benutzerdefinierte Felder',
        customwebaddress: 'Eigene Domain verbinden',
        deactivateDeleteCommunity: 'Community deaktivieren/löschen',
        customise: 'Anpassen',
        customization: 'Anpassung',
        customize: 'Anpassen',
        customizeaplan: 'Tarif anpassen',
        customizebuiltindomain: 'Integrierte Domain anpassen',
        dashboard: 'Dashboard',
        dataprocessingaddendum:
            'Auftragsverarbeitungsvertrag (AVV) und Anhang technische und organisatorische Maßnahmen (TOMs)',
        date: 'Datum',
        dateempty: 'Kein Datum gewählt',
        dateoverride: 'Datumsüberschreibung',
        datesandtimes: 'Daten und Zeiten',
        day: 'Tag | Tage',
        daybookingconfiguration: 'Tagesbuchungskonfiguration',
        days: 'Tage',
        default: 'Standard',
        defaultplan: 'Standardplan',
        defaults: 'Standardwerte',
        defaultsforimportedevents: 'Standardwerte für importierte Events',
        delete: 'Löschen',
        deletemethod: 'Methode löschen',
        deleteresource: 'Ressource löschen',
        deleterow: 'Zeile löschen',
        deletetimepass: 'Zeitpass löschen',
        deleteuser: 'Benutzer löschen',
        description: 'Beschreibung',
        descriptionoptional: 'Beschreibung (optional)',
        details: 'Details',
        discard: 'Abbrechen',
        discount: 'Rabatt',
        domain: 'Domain',
        doorsandkeys: 'Türen & Schlüssel',
        download: 'Herunterladen',
        downloadcsvtemplate: 'CSV-Vorlage herunterladen',
        downloadGerman: 'Deutsch herunterladen',
        downloadEnglish: 'Englisch herunterladen',
        draft: 'Entwurf',
        dragdropimage: 'Ziehe ein Bild hierher oder klicke zum Auswählen.',
        duplicate: 'Duplizieren',
        duration: 'Dauer',
        durationhours: 'Dauer (Stunden)',
        edit: 'Bearbeiten',
        editbookingresourcegroup: 'Buchungsressourcengruppe bearbeiten',
        editrow: 'Zeile bearbeiten',
        editrowwithnumber: 'Zeile {number} bearbeiten',
        editsetup: 'Einrichtung bearbeiten',
        edittext: 'Text bearbeiten',
        email: 'E-Mail',
        emailconfirmation: 'E-Mail-Bestätigung',
        emailfooter: 'E-Mail-Footer/Disclaimer',
        emailisconfirmed: 'E-Mail ist bestätigt',
        emailstonotify: 'E-Mails zur Benachrichtigung bei Buchungen',
        embed: 'Einbetten',
        embedcode: 'Einbettungscode',
        enabled: 'aktiviert',
        enableintegration: 'Integration aktivieren',
        end: 'Ende',
        enddate: 'Enddatum',
        ended: 'Beendet',
        endtime: 'Endzeit',
        event: 'Veranstaltung',
        eventFormat: 'Wo findet diese Veranstaltung statt?',
        eventParticipation: 'Wie möchtest du die Teilnahme an dieser Veranstaltung organisieren?',
        eventcreate: '@:labels.event @.lower:labels.create',
        eventcreated: '@:labels.event @.lower:labels.created',
        eventdelete: '@:labels.event @.lower:labels.delete',
        eventedit: '@:labels.event @.lower:labels.edit',
        eventOngoing: 'Findet statt',
        events: 'Veranstaltungen',
        eventsall: 'Alle Veranstaltungen',
        eventsnext: 'Kommende Veranstaltungen',
        eventspast: 'Vergangene Veranstaltungen',
        eventsupcoming: 'Als nächstes',
        eventsupcomingpublic: 'Nächste öffentliche Veranstaltungen',
        eventticketpayments: 'Veranstaltungsticket-Zahlungen',
        eventtime: 'Zeit',
        eventtypes: 'Veranstaltungstypen',
        eventyour: 'deine @:labels.event',
        everyone: 'Alle',
        excluding: 'zzgl.',
        exclusive: 'Exklusiv',
        exit: 'Beenden',
        expiration: 'Ablauf',
        expiresAt: 'gültig bis',
        externalregistrationlink: 'Externer Registrierungslink',
        externalregistration: 'Externe Registrierung',
        externalbookingid: 'Externe Buchungs-ID',
        failedGeneric: '@:errors.failedGeneric',
        // 'An error occurred. Please try again.',
        failedToSaveChanges: '@:errors.failedToSaveChanges',
        favicon: 'Favicon',
        features: 'Funktionen',
        feed: 'Feed',
        female: 'Weiblich',
        fileadd: 'Datei hinzufügen',
        files: 'Dateien',
        finish: 'Abschließen',
        firstinvoice: 'Erste Rechnung',
        firstthingsfirst: 'Zuerst das Wichtigste',
        follow: 'Folgen',
        follower: 'keine Follower | ein Follower | {n} Follower',
        followerrole: 'Follower:in',
        followers: 'Follower',
        following: 'Du folgst',
        followingday: 'Folgetag',
        free: 'kostenlos',
        freetrial: 'Kostenlose Testphase',
        freeticket: 'Kostenloses Ticket',
        from: 'Von',
        fromprice: 'Ab',
        gender: 'Gender/Anrede',
        general: 'Allgemein',
        generalinformation: 'Allgemeine Informationen',
        getstarted: 'Hier starten',
        getstartedwithtaxrates: 'Starte mit Steuersätzen',
        getstartedwithupgrades: 'Starte mit Upgrades',
        getticket: 'Tickets',
        going: 'Du gehst hin',
        goingcount: 'geht hin | gehen hin',
        googlecalendar: 'Google Kalender',
        gotosettings: 'Zu den Einstellungen',
        gotosignupsettings: 'Zu den Anmeldeeinstellungen',
        group: 'Gruppe',
        groupby: 'Gruppiert nach {subject}',
        groupmember: 'Gruppenmitglied | Gruppenmitglieder',
        groupname: 'Gruppenname',
        groupnew: 'Neue @:labels.group',
        guest: 'Gast',
        hashtag: 'Hashtag',
        hashtagadd: '@:labels.hashtag @.lower:labels.add',
        headline: 'Überschrift',
        help: 'Hilfe',
        helparticle: 'Hilfeartikel',
        helparticles: 'Hilfeartikel',
        helpurl: 'Hilfe-URL',
        here: 'hier',
        hidden: 'Ausgeblendet',
        hide: 'Ausblenden',
        hideevent: 'Event ausblenden',
        hideinupcominglists: 'In Upcoming-Listen ausblenden',
        hour: 'Stunde | Stunden',
        hours: 'Stunden',
        hourshort: 'h',
        id: 'ID',
        idealwero: 'Wero',
        identityproviders: 'Identity Provider',
        image: 'Bild',
        imageadd: '@:labels.image @.lower:labels.add',
        imageguidelines: 'Bildrichtlinien',
        imageSection: 'Bildabschnitt',
        import: 'Importieren',
        imprint: 'Impressum',
        inactive: 'Inaktiv',
        inapp: 'In-App',
        including: 'inkl.',
        inclusive: 'Inklusiv',
        information: 'Information',
        integrations: 'Integrationen',
        interested: 'Interesse bekunden',
        interestedpeople: 'Interessenten',
        interestedsent: 'Anfrage gesendet',
        invalid: 'ungültig',
        invite: 'Einladen',
        invited: 'Eingeladen',
        invitedmembers: 'Eingeladene Mitglieder',
        invitemember: 'Mitglied einladen',
        invoice: 'Rechnung',
        invoiceNumber: 'Rechnungsnummer',
        invoicenext: 'Nächste Rechnung',
        invoicepreview: 'Rechnungsvorschau',
        invoices: 'Rechnungen',
        itemscount: 'keine Einträge | 1 Eintrag | {n} Einträge',
        javascript: 'JavaScript',
        javascriptsnippet: 'JavaScript-Snippet',
        join: 'Beitreten',
        keepBooking: 'Buchung behalten',
        key: 'Schlüssel',
        keyassign: 'Weise einen @.lower:labels.key zu',
        keycreate: '@:labels.create @.lower:labels.key',
        keyimport: 'Importieren',
        keymanagement: 'Schlüsselverwaltung',
        keynotassigned: 'Kein @.lower:labels.key zugewiesen',
        keys: 'Schlüssel',
        language: 'Sprache',
        last4digits: 'Letzte vier Ziffern',
        lastactivity: 'Letzte Aktivität',
        lastupdatedat: 'Zuletzt aktualisiert am',
        leave: 'Verlassen',
        legacy: 'Legacy',
        legacypricing: 'Legacy-Preise',
        linkedbookingid: 'Verknüpfte Buchungs-ID',
        letsgetstarted: "Los geht's.",
        letstalkabout: 'Ich unterhalte mich gerne über',
        limitUsersAlmostReached: 'Mitglieder-Limit fast erreicht',
        limitUsersReached: 'Du hast das Mitglieder-Limit erreicht',
        link: 'Link',
        linkadd: '@:labels.link @.lower:labels.add',
        linkcopy: '@:labels.link @.lower:labels.copy',
        linkInvalid: 'Link ungültig',
        linkInvalidDescription: 'Dieser Link ist ungültig.',
        links: 'Links',
        list: 'Liste',
        loaded: 'geladen',
        loading: 'laden',
        loadmore: 'Mehr laden',
        lock: 'Schließen',
        location: 'Ort',
        locationcreate: 'Standort erstellen',
        locationedit: 'Standort bearbeiten',
        locations: 'Standorte',
        locks: 'Schlösser',
        pin: 'PIN',
        // used in admin menu
        login: 'Login',
        loginctatitle: 'Login-CTA-Titel',
        logout: 'Ausloggen',
        kinescopeVideoPlayer: 'Kinescope-Videoplayer',
        loomVideoPlayer: 'Loom-Videoplayer',
        makeadmin: 'Zum Admin machen',
        male: 'Männlich',
        managecustomervatsettings: 'USt-Einstellungen verwalten',
        managesubscription: 'Abonnement verwalten',
        markallasread: 'Alle als gelesen markieren',
        markasfeatured: 'Als Featured markieren',
        markassticky: 'Als Sticky markieren',
        market: 'Markt',
        manage: 'Verwalten',
        matching: 'Matching',
        marketitem: 'Marktartikel',
        marketitemclose: '@:labels.marketitem @.lower:labels.close',
        marketitemcreate: '@:labels.marketitem @.lower:labels.create',
        marketitemdelete: '@:labels.marketitem @.lower:labels.delete',
        marketitemedit: '@:labels.marketitem @.lower:labels.edit',
        marketitems: '@:labels.marketitem',
        marketitemsempty: 'Noch keine Marktartikel erstellt.',
        marketitemyour: 'deinen @:labels.marketitem',
        matchingprofiles: 'Matchende Profile',
        maxamount: 'Max. Anzahl',
        maxbookableamount: 'Max. buchbare Menge',
        maxbookingduration: 'Maximale Buchungsdauer',
        member: 'Mitglied | Mitglieder',
        memberadd: 'Mitglied hinzufügen',
        memberalreadymembership:
            'Dieser Benutzer hat einen aktuellen und/oder zukünftigen Mitgliedschaftstarif. Benutzer mit einem gültigen Tarif können keiner Mitgliedschaft eines anderen Benutzers hinzugefügt werden.',
        membercreate: 'Mitglied erstellen',
        members: 'Mitglieder',
        membership: 'Mitgliedschaft',
        membershipcreate: 'Mitgliedschaft erstellen',
        membershipdelete: 'Mitgliedschaft löschen',
        membershipedit: 'Mitgliedschaft bearbeiten',
        membershippayments: 'Mitgliedschaftszahlungen',
        membershipUpdate: {
            noPlanSelected: 'Kein Tarif ausgewählt',
            payForAnotherMember: 'Für ein weiteres Mitglied bezahlen',
            payingFor: '{name} bezahlt für',
            proration: 'Anteilsberechnung',
            prorationDescription:
                'Passt den Rechnungsbetrag an, um die Differenz zwischen dem Aktualisierungsdatum des Abonnements und dem Ankerdatum des Abrechnungszyklus zu berücksichtigen. Stellt sicher, dass Kund:innen nur für die Zeit berechnet werden, in der sie den aktualisierten Tarif nutzen.',
            removePlan: 'Tarif entfernen',
            removePlanWarning:
                'Diese Mitgliedschaft bezahlt für andere Mitglieder (Untertarife). Wenn du sie entfernst, werden alle zugehörigen Untertarife automatisch gekündigt, sobald du speicherst.',
            selectAPlan: 'Tarif auswählen',
            updateSubscription: 'Abonnement aktualisieren',
        },
        membershipupgrade: 'Mitgliedschaft upgraden',
        membershipupgrades: 'Mitgliedschafts-Upgrades',
        membershipyour: 'Deine Mitgliedschaft',
        membersmanage: 'Mitglieder verwalten',
        mentionsmo: 'Person erwähnen',
        message: 'Nachricht',
        messagenew: 'Neue @:labels.message',
        messenger: 'Nachrichten',
        minutes: 'Minuten',
        newconversation: 'Neue Unterhaltung',
        minamount: 'Min. Anzahl',
        minbookableamount: 'Min. buchbare Menge',
        minbookingduration: 'Minimale Buchungsdauer',
        mine: 'Meins',
        monetization: 'Monetarisierung',
        monetizationCollection: {
            subscriptions: {
                billing: 'Abrechnung',
                // the minimum time before the next billing cycle to for subscribers to cancel their subscription
                cancelnotice: 'Kündigungsfrist',
                invoices: 'Rechnungen',
                minduration: 'Mindestvertragslaufzeit',
                monetization: 'Monetarisierung',
                paymentmethods: '@:labels.paymentmethods',
                pricing: 'Preise',
            },
        },
        month: 'Monat',
        more: 'Mehr',
        morecount: '{count} weitere',
        moreoptions: 'Weitere Optionen',
        my: 'Meine',
        mybookings: '@:labels.my @:labels.bookings',
        myevents: '@:labels.my @:labels.events',
        mypages: '@:labels.my @:labels.pages',
        mypastevents: 'Meine vergangenen Veranstaltungen',
        myticket: 'Mein Ticket',
        mytickets: 'Meine Tickets',
        myticketsfor: 'Meine Tickets für "{event}"',
        mywallet: 'Meine Pässe',
        name: 'Name',
        namefirst: 'Vorname',
        namelast: 'Nachname',
        nameyoureventtype: 'Benenne deinen Event-Typ',
        net: 'Netto',
        netprice: 'Nettopreis',
        netpricepercycle:
            'Nettopreis pro Abrechnungszeitraum. Zur Berechnung des Bruttopreises wird der für diese Ressource hinterlegte Steuersatz verwendet.',
        numberofblockingslots: 'Blockierte Slots',
        never: 'Nie',
        new: 'Neu',
        next: 'Weiter',
        nextinvoice: 'Nächste Rechnung',
        no: 'Nein',
        generaladmission: 'Allgemeiner Eintritt',
        noactivesubscription: 'Kein aktives Abonnement',
        nonbinary: 'Divers',
        none: 'keine',
        nooptions: 'Keine Optionen',
        noresults: 'Keine Ergebnisse',
        notavailable: 'k. A.',
        notdefined: 'nicht definiert',
        note: 'Notiz',
        noteadd: 'Notiz hinzufügen',
        notgrouped: 'Nicht gruppiert',
        notificationpreferences: 'Benachrichtigungen konfigurieren',
        notifications: 'Benachrichtigungen',
        notificationspause: 'Benachrichtigungen pausieren',
        notificationspaused: 'Benachrichtigungen pausiert',
        notifymewhen: 'Benachrichtige mich, wenn',
        notifymewhenasadmin: 'Als Administator',
        notprovided: 'nicht angegeben',
        nuki: 'NUKI',
        numberoftickets: 'Anzahl der Tickets',
        oauth2: 'OAuth 2.0',
        of: 'von',
        ok: 'OK',
        option: 'Option',
        options: 'Optionen',
        nooptionsavailable: 'Keine Optionen verfügbar',
        optionsdisplay: 'Anzeigeoptionen',
        or: 'oder',
        ordercheckout: 'Zur Kasse',
        ordercompletesubcription: 'kostenpflichtig abschließen',
        ordercompletesubcriptionfree: 'kostenlos abschließen',
        orderconfirm: 'Bestellung abschließen',
        orderreview: 'Bestellung überprüfen',
        ordersummary: 'Bestellübersicht',
        organizer: 'Organisator:in',
        organizers: 'Organisierende',
        organizersadd: '@:labels.organizers @.lower:labels.add',
        others: 'niemand | eine weitere | {n} weitere',
        overwriteprice: 'Preis überschreiben',
        owner: 'Inhaber:in',
        owners: 'Inhaber',
        ownersadd: 'Eigentümer hinzufügen',
        page: 'Seite',
        pagecreate: '@:labels.page @.lower:labels.create',
        pagecreated: '@:labels.page @.lower:labels.created',
        pagedelete: '@:labels.page @.lower:labels.delete',
        pageedit: '@:labels.page @.lower:labels.edit',
        pagename: 'Seitenname',
        pagepublish: '@:labels.page @.lower:labels.publish ',
        pagepublished: '@:labels.page @.lower:labels.published ',
        pages: 'Seiten',
        pagesempty: 'Noch keine Seiten erstellt.',
        pagesmy: '@:labels.mypages',
        pageyour: 'deine @:labels.page',
        paid: 'bezahlt',
        purchased: 'Gekauft',
        paidby: 'Bezahlt von',
        participants: 'Teilnehmende',
        password: 'Passwort',
        passwordreset: 'Passwort zurücksetzen',
        passwordsave: 'Passwort speichern',
        passwordsetnew: 'Neues Passwort festlegen',
        pause: 'Pausieren',
        pay: 'Bezahlen',
        payforanothermember: 'Für ein weiteres Mitglied bezahlen',
        payment: 'Zahlung',
        paymentfees: 'Zahlungsgebühren',
        paymentmethod: 'Zahlungsmethode',
        paymentmethodadd: '@:labels.paymentmethod @.lower:labels.add',
        paymentmethoddelete: '@:labels.paymentmethod @.lower:labels.delete',
        paymentmethods: 'Zahlungsmethoden',
        payments: 'Zahlungen',
        per: 'Pro',
        permissions: 'Berechtigungen',
        permissionsEveryoneInCommunity: 'Alle in der Community',
        permissionsGroup: {
            approval: 'Beitritt nur mit Genehmigung',
            inviteonly: 'Beitritt nur auf Einladung',
            open: 'Jede:r kann beitreten',
            permissions: 'Berechtigungen',
        },
        permissionsOnlyAdministrators: 'Nur Administrator:innen',
        permissionsAdministratorsAndMembers: 'Administrator:innen und Mitglieder',
        permissionsAllMembers: 'Alle Mitglieder',
        permissionsMembersWithPlans: 'Mitglieder mit bestimmten Tarifen',
        permissionsRule: 'Wer darf das tun?',
        permissionsAddRule: 'Regel hinzufügen',
        permissionsRemoveRule: 'Regel entfernen',
        permissionsCustomField: 'Bezeichner des benutzerdefinierten Feldes',
        permissionsValues: 'Erlaubte Werte',
        permissionsValuesPlaceholder: 'Kommagetrennte Werte eingeben',
        permissionsActions: {
            eventsCreate: 'Wer darf Veranstaltungen erstellen?',
            pagesCreate: 'Wer darf Seiten erstellen?',
            feedPost: 'Wer darf im Feed posten?',
        },
        permissionsRules: {
            role: 'Administrator:innen oder Mitgliederrollen',
            plan_id: 'Mitgliedschaftstarif',
            custom: 'Benutzerdefiniertes Profilfeld',
            everyone: 'Alle',
        },
        permissionsSeeAllAdmins: 'Alle Administrator:innen anzeigen',
        permissionsWhoCanCreateEvents: 'Wer darf Veranstaltungen erstellen',
        phonenumber: '@:labels.phoneNumber',
        phoneNumber: 'Telefonnummer',
        phonenumbernotprovided: '@:labels.phonenumber @:labels.notprovided',
        pinadd: 'Anpinnen',
        pinremove: 'Pin entfernen',
        placeholder: 'Platzhalter',
        plan: 'Tarif',
        plancancel: 'Tarif kündigen',
        plancancelledto: 'Tarif gekündigt zum',
        planchange: 'Tarif ändern',
        planchangecancel: 'Tarifwechsel stornieren',
        planchoose: 'Tarif wählen',
        plancurrent: 'Dein @:labels.plan',
        plandetails: 'Tarifdetails',
        planedit: 'Tarif bearbeiten',
        planmanage: 'Tarif verwalten',
        plannew: 'Neuer @:labels.plan',
        planreactivate: 'Kündigung rückgängig machen',
        plans: 'Tarife',
        planselection: 'Wähle deinen Tarif',
        planupcoming: 'Dein neuer Tarif',
        planupcomingstart: 'Neuer Tarif startet am',
        planupdate: 'Tarifaktualisierung',
        planupgradechose: 'Wähle deine Upgrades',
        planupgrades: 'Tarif-Upgrades',
        policies: 'Richtlinien',
        policy: 'Richtlinie',
        policyimport: 'Importieren',
        policynotassigned: 'Keine Richtlinie zugewiesen',
        post: 'Beitrag',
        postalcode: 'Postleitzahl',
        postbtn: 'Posten',
        postedit: '@:labels.post @.lower:labels.edit',
        postpin: 'Beitrag anpinnen',
        postpublished: '@:labels.post @.lower:labels.published',
        posts: 'Beiträge',
        postyour: 'deinen @:labels.post',
        prefernottosay: 'Möchte ich nicht angeben',
        preview: 'Vorschau',
        previous: 'Zurück',
        paidAccessStartsHere: 'Exklusive Inhalte beginnen unterhalb',
        premiumAccessIncludedInYourPlan: 'Exklusive Inhalte sind in deinem Tarif enthalten',
        paywallSection: 'Paywall-Abschnitt',
        price: 'Preis',
        priceandlimits: 'Preis und Limits',
        priceandterms: 'Preis und Bedingungen',
        pricebeforediscount: 'Preis vor Rabatt',
        pricedisplay: 'Preis',
        pricegross: 'Brutto',
        pricein: 'Preis in {currency}',
        pricenet: 'Netto',
        priceperday: 'Preis pro Tag',
        priceperhour: 'Preis pro Stunde',
        priceperslot: 'Preis pro Slot',
        pricepreview: 'Preisvorschau',
        priceoverride: 'Preisüberschreibung',
        pricesubtotal: 'Zw.Summe',
        pricetotal: 'Gesamt',
        pricevaries: 'Preise variieren',
        primarycolor: 'Primärfarbe (Markenfarbe)',
        privacypolicy: 'Datenschutzerklärung',
        private: 'Privat',
        profile: 'Profil',
        profileedit: '@:labels.profile @.lower:labels.edit',
        profilename: 'Profilname',
        profilepublish: '@:labels.profile @.lower:labels.publish',
        profilepublished: '@:labels.profile @.lower:labels.published',
        profileupdated: '@:labels.profile @.lower:labels.updated',
        projects: 'Projekte',
        provider: 'Provider',
        public: 'Öffentlich',
        publish: 'Veröffentlichen',
        published: 'Veröffentlicht',
        push: 'Push',
        pushnotification: 'Push-Benachrichtigung',
        quantity: 'Anzahl',
        rate: 'Satz',
        rateinpercent: 'Satz in %',
        ratetype: 'Satztyp',
        readhelparticle: 'Hilfsartikel lesen',
        readless: 'Weniger lesen',
        readmore: 'Mehr lesen',
        redeem: 'Einlösen',
        redeemable: 'Einlösbar',
        redeemed: 'Eingelöst',
        redirecturl: 'Redirect-URL',
        refresh: 'neu laden',
        refund: 'Rückerstattung',
        refunded: 'rückerstattet',
        refundedamount: 'Rückerstatteter Betrag',
        refundedat: 'Rückerstattet am',
        refundUnusedMembershipTime: 'Ungenutzte Mitgliedschaftszeit erstatten',
        region: 'Region',
        registrationadditionalinfo: 'Reg.: Zusatzinfo',
        registrationaddress: 'Meldeadresse',
        registrationaddressedit: '@:labels.registrationaddress @.lower:labels.edit ',
        registrationcity: 'Meldeadresse: Stadt',
        registrationcompany: 'Meldeadresse: Firma',
        registrationconfirmation: 'Registrierungsbestätigung',
        registrationcountry: 'Meldeadresse: Land',
        registrationlink: 'Öffentlicher Registrierungslink',
        registrationpostalcode: 'Meldeadresse: Postleitzahl',
        registrationstreet: 'Meldeadresse: Strasse',
        registrationphone: 'Meldeadresse: Telefon',
        reject: 'Ablehnen',
        remaining: 'übrig',
        remove: 'Entfernen',
        removeapikey: 'API-Schlüssel entfernen',
        renewsannually: 'Verlängert sich jährlich',
        renewsmonthly: 'Verlängert sich monatlich',
        renewsubscription: 'Abonnement erneuern, um coapp weiter zu nutzen.',
        reopen: 'Wieder öffnen',
        reopenItem: 'Anzeige öffnen',
        reply: 'Antworten',
        reportbug: 'Fehler melden',
        requestapprovalfromyourmembers: 'Bestätigung von deinen Mitgliedern anfordern',
        required: 'Pflichtfeld',
        reset: 'Zurücksetzen',
        resolve: 'Lösen',
        resource: 'Ressource',
        resourcecategories: 'Ressourcenkategorien',
        resourceid: 'Ressourcen-ID',
        resourcename: 'Ressourcenname',
        resources: 'Ressourcen',
        revenue: 'Einnahmen',
        review: 'Zusammenfassung',
        revokeadminrights: 'Adminrechte entziehen',
        role: 'Rolle',
        rightOfWithdrawalForm: 'Vertrag widerrufen',
        roles: {
            admin: 'Admin',
            user: 'Nutzer:in',
        },
        rules: {
            add_condition: 'Bedingung hinzufügen',
            add_group: 'Regel hinzufügen',
            conditions: {
                AND: 'und',
                OR: 'oder',
            },
            fields: {
                plan_id: 'Tarif',
                user_email: 'Benutzer-E-Mail',
                user_id: 'Benutzer',
                user_role: 'Benutzerrolle',
                user_status: 'Benutzerstatus',
            },
            operators: {
                ANY: 'beliebig',
                BETWEEN: 'ist zwischen',
                EQ: 'ist gleich',
                GT: 'größer als',
                GTE: 'größer oder gleich',
                LIKE: 'enthält',
                LT: 'kleiner als',
                LTE: 'kleiner oder gleich',
                NEQ: 'ist nicht gleich',
                NOTLIKE: 'enthält nicht',
            },
            remove_condition: 'Bedingung entfernen',
        },
        run: 'Ausführen',
        runprogress: 'Fortschritt',
        salutation: 'Anrede',
        salutationfemale: 'Frau',
        salutationmale: 'Herr',
        salutationnonbinary: 'Divers',
        save: 'Speichern',
        saveandpublish: 'Speichern und veröffentlichen',
        saveasdraft: 'Als Entwurf speichern',
        saveasicsfile: 'Als ICS speichern',
        saveChanges: 'Änderungen speichern',
        saved: 'Gespeichert',
        schedulecancellation: 'Kündigung planen',
        scopes: 'Scopes',
        scrolltotop: 'Nach oben',
        search: 'Suche',
        secondarycolor: 'Sekundärfarbe (Linkfarbe)',
        seeall: 'Alle ansehen',
        seemore: 'Mehr anzeigen',
        select: 'Auswählen',
        selectaccountcalendar: 'Konto auswählen, das Zugriff auf deinen Kalender hat',
        selectall: 'Alle auswählen',
        selectcalendartosync: 'Kalender zur Synchronisierung auswählen',
        selected: 'Ausgewählt',
        selectcolumns: 'Spalten auswählen',
        selectedupgrade: 'Ausgewähltes Upgrade',
        selectlock: 'Schlösser auswählen',
        selectlockDescription:
            'Du kannst ein oder mehrere Schlösser auswählen und festlegen, wie lange vor Buchungsbeginn und nach Buchungsende der Zutritt möglich ist.',
        selectplan: 'Tarif auswählen',
        userFilters: {
            after: 'ist nach',
            before: 'ist vor',
            between: 'ist zwischen',
            clearFilters: 'Filter zurücksetzen',
            equal: 'ist gleich',
            fieldPlaceholder: 'Feld auswählen',
            filterField: 'Feld',
            from: 'Von',
            greaterThan: 'ist größer als',
            greaterThanOrEqual: 'ist größer oder gleich',
            inUsersValues: 'ist eines von',
            lessThan: 'ist kleiner als',
            lessThanOrEqual: 'ist kleiner oder gleich',
            like: 'enthält',
            filter: 'Filter',
            addFilter: 'Filter hinzufügen',
            notEqual: 'ist nicht gleich',
            notInUsersValues: 'ist keines von',
            notLike: 'enthält nicht',
            operator: 'Operator',
            operatorPlaceholder: 'Operator auswählen',
            planPlaceholder: 'Tarif auswählen',
            statusPlaceholder: 'Status auswählen',
            to: 'Bis',
            value: 'Wert',
            valuePlaceholder: 'Wert eingeben',
        },
        selectresource: 'Ressource auswählen',
        selectresources: 'Ressourcen auswählen',
        send: 'Senden',
        sendfeaturerequest: 'Feature-Vorschlag senden',
        sendinvoice: 'Rechnung senden',
        sepa: 'SEPA',
        sepaaccount: 'SEPA-Konto',
        sepadirectdebit: 'SEPA Lastschrift',
        sepaHelpArticleTitle: "'Wichtige Hinweise zur Annahme von SEPA-Lastschriftzahlungen für Mitgliedschaften'",
        sepapayments: 'SEPA-Zahlungen',
        setAsDefault: 'Als Standard festlegen',
        setcreditsamountoptional: 'Credits-Betrag festlegen (optional)',
        setmaxbookableamount: 'Max. buchbare Menge festlegen',
        setminbookableamount: 'Min. buchbare Menge festlegen',
        settings: 'Einstellungen',
        setuppayments: 'Zahlungen einrichten',
        sameday: 'Gleicher Tag',
        share: 'Teilen',
        shareableregistrationlink: 'Teilbarer Registrierungslink',
        shareinfeed: 'Im Feed teilen',
        showdetails: 'Details anzeigen',
        showmore: 'Mehr anzeigen',
        signup: 'Registrieren',
        suggestedChats: 'Vorgeschlagene Chats',
        signupApproval: 'Genehmigungsbasiert',
        signupctatitle: 'Sign-up-CTA-Titel',
        signupInviteOnly: 'Nur per Einladung',
        signupOpen: 'Jeder kann sich registrieren',
        signupstrategy: 'Registrierungsstrategie',
        skip: 'Überspringen',
        slots: 'Slots',
        slug: 'Slug',
        sold: 'Verkauft',
        soldout: 'Ausverkauft',
        sortbyselected: 'Nach Auswahl sortieren',
        space: 'Raum',
        spacecolors: 'Space-Farben',
        spaceid: 'Space-ID',
        spaceintroduction: 'Space-Einführung',
        spacelogo: 'Space-Logo',
        start: 'Start',
        startdate: 'Startdatum',
        startsync: 'Sync starten',
        starttime: 'Startzeit',
        statistics: 'Statistiken',
        status: 'Status',
        step: 'Schritt {number}',
        stripe: 'Stripe',
        stripeaccount: 'Stripe-Konto',
        stripecustomer: 'Stripe-Kunde',
        stripedashboard: 'Stripe-Dashboard',
        stripepaymentfees: 'Stripe-Zahlungsgebühren',
        stripepaymentmethods: 'Stripe-Zahlungsmethoden',
        strongtags: 'Strong Tags',
        strongtagssuggestion: 'Strong-Tags-Vorschlag',
        submit: 'Senden',
        subscribenow: 'Jetzt abonnieren',
        subscriptioncancelssoon: 'Dein Abonnement wird bald gekündigt',
        subscriptioncreate: 'Mitgliedschaft abschließen',
        subscriptionend: 'Abonnementende',
        subscriptionpreview: 'Abonnementvorschau',
        subscriptionSchedule: {
            addFreeTrial: 'Kostenlose Testphase hinzufügen',
            anchorDate: 'Stichtag',
            automaticCharge: 'Standard-Zahlungsmethode des Nutzers automatisch belasten',
            billingCycle: 'Abrechnungszeitraum',
            configurationQuestion: 'Wann soll diese Konfiguration wirksam werden?',
            customDate: 'Benutzerdefiniertes Datum',
            emailInvoice: 'Rechnung per E-Mail an den Kunden senden, um manuell zu zahlen',
            endOfBillingCycle: 'Ende des Abrechnungszeitraums',
            endOfBillingCycleWithCancellation:
                'Ende des Abrechnungszeitraums unter Berücksichtigung der Kündigungsfrist',
            forever: 'Unbefristet',
            freeTrialDays: 'Kostenlose Testtage',
            now: 'Jetzt',
            paymentCollectionMethod: 'Zahlungsart',
            resetBillingCycle: 'Abrechnungszeitraum zurücksetzen',
            resetBillingCycleDisabled:
                'Abrechnungszeitraum kann während einer aktiven Testphase nicht zurückgesetzt werden',
            resetBillingCycleInfo:
                'Wenn aktiviert, wird der Stichtag für den Abrechnungszeitraum am Datum der Wirksamkeit dieser Aktualisierung zurückgesetzt.',
            scheduleUpdate: 'Zeitplanaktualisierung',
            startEndDate: 'Start- und Enddatum',
            title: 'Abonnement-Zeitplan',
            trialEndsAt: 'Testphase endet am',
        },
        subscriptionstart: 'Abonnementstart',
        subscriptionstatus: 'Abonnementstatus',
        subtitle: 'Untertitel',
        suggestedcontent: 'Vorgeschlagene Inhalte',
        summary: 'Zusammenfassung',
        suppresswelcomeemail: 'Willkommens-E-Mail unterdrücken',
        suspensionreason: 'Sperrgrund',
        synchronize: 'Synchronisieren',
        synchronizecalendar: 'Kalender synchronisieren',
        systemid: '{systemname} System-ID',
        systemuserid: '{systemname} Systembenutzer-ID',
        tags: 'Tags',
        tax: 'Steuern',
        taxrate: 'Steuersatz',
        taxrates: 'Steuersätze',
        taxtype: 'Steuertyp',
        terms: 'Bedingungen',
        termsandconditions: 'Nutzungsbedingungen',
        termsandconditionslink: 'Link zu den Nutzungsbedingungen (optional)',
        termsofservice: '@:labels.termsandconditions',
        testconnection: 'Verbindung testen',
        text: 'Text',
        textcolor: 'Textfarbe',
        textSection: 'Textabschnitt',
        theeventscalendar: 'The Events Calendar',
        thisevent: 'Dieses Event',
        ticket: 'Ticket',
        ticketcode: 'Ticketcode',
        ticketcreate: 'Ticket erstellen',
        ticketedit: 'Ticket bearbeiten',
        tickets: 'Tickets',
        ticketsmore: 'Weitere Tickets',
        ticketstatus: 'Ticketstatus',
        ticketurl: 'Ticket-Link',
        time: 'Zeit',
        timepass: 'Zeitpass',
        timepassCard: {
            currentlyActive: 'Derzeit aktiv',
            left: '{count} übrig',
            useConfirmation: 'Wenn du fortfährst, wird dieser Pass sofort aktiviert und eingelöst. Bist du sicher?',
            useNow: 'Jetzt verwenden',
        },
        timepasses: 'Zeitpässe',
        times: 'Mal | Mal',
        timezone: 'Zeitzone',
        title: 'Titel',
        to: 'Bis',
        today: 'Heute',
        tokenurl: 'Token-URL',
        topics: 'Themen',
        total: 'Summe',
        tryagain: 'Erneut probieren',
        type: 'Typ',
        unarchive: 'Archivierung aufheben',
        unavailable: 'nicht verfügbar',
        undocancellation: 'Kündigung rückgängig machen',
        unfollow: 'Entfolgen',
        unit: 'Einheit',
        unknown: 'Unbekannt',
        unlock: 'Öffnen',
        unsavedchanges: 'Ungespeicherte Änderungen',
        update: 'Update',
        updatecreate: '@:labels.update @.lower:labels.create',
        updated: 'Aktualisiert',
        updatedat: 'Aktualisiert am',
        updatedelete: '@:labels.update @.lower:labels.delete',
        updatedon: '@:labels.updated am',
        requestPins: 'PINs anfordern',
        updateedit: '@:labels.update @.lower:labels.edit',
        updates: 'Updates',
        usedcashcredits: 'Verwendetes Barguthaben',
        usedtimecredits: 'Verwendete Zeitguthaben',
        upgrade: 'Upgrade',
        upgradecoappsubscription: 'coapp-Abonnement upgraden',
        upgrades: 'Upgrades',
        upgradeSubscription: 'Tarif-Upgrade',
        upgradetounlock: 'Upgrade zum Freischalten',
        uploadcsv: 'CSV hochladen',
        uploadnewpicture: 'Neues Bild hochladen',
        user: 'Nutzer:in',
        userinfourl: 'User-Info-URL',
        username: 'Benutzername',
        usernotavailable: 'Nicht verfügbar',
        usershowmatchingprofiles: 'Matches anzeigen',
        userssome: 'Bestimmte Nutzer',
        valid: 'gültig',
        validfor: 'gültig für',
        validfrom: 'Gültig ab',
        validminutesafterbooking: 'Erlaube den Zutritt nach Buchungsende (in Minuten)',
        validminutesbeforebooking: 'Erlaube den Zutritt vor Buchungsbeginn (in Minuten)',
        validto: 'Gültig bis',
        validuntil: 'Gültig bis',
        expired: 'Abgelaufen',
        vat: 'MwSt.',
        vatid: 'USt-IdNr.',
        vatidnotprovided: 'keine USt-IdNr. angegeben',
        versionfrom: 'Version vom',
        view: 'Ansehen',
        viewcurrentpaymentmethods: 'Aktuelle Zahlungsmethoden anzeigen',
        vieweventdetails: 'Veranstaltungsdetails ansehen',
        viewprofile: 'Profil anzeigen',
        viewswitcher: 'Ansichtsauswahl',
        viewupgradeoptions: 'Upgrade-Optionen ansehen',
        visibility: 'Sichtbarkeit',
        visible: 'Sichtbar',
        videoSection: 'Videoabschnitt',
        videoUrl: 'Video-URL',
        tiktokVideoPlayer: 'TikTok-Videoplayer',
        vimeoVideoPlayer: 'Vimeo-Videoplayer',
        mixcloudAudioPlayer: 'Mixcloud-Audioplayer',
        podigeeAudioPlayer: 'Podigee-Podcast-Player',
        soundcloudAudioPlayer: 'SoundCloud-Audioplayer',
        spotifyAudioPlayer: 'Spotify-Audioplayer',
        wallet: 'Pässe',
        warning: 'Warnung',
        week: 'Woche',
        welcomeimage: 'Willkommensbild',
        welcometocoapp: 'Willkommen bei coapp',
        welcometocommunity: 'Willkommen in der Community!',
        whocanbookresource: 'Wer kann diese Ressource buchen',
        whocanseeresource: 'Wer kann diese Ressource sehen',
        year: 'Jahr',
        yes: 'Ja',
        you: 'Du',
        yourreply: 'Deine Antwort',
        youtubeVideoPlayer: 'YouTube-Videoplayer',
        zapier: 'Zapier',
    },
    languages: {
        de: 'Deutsch',
        en: 'Englisch',
    },
    messages: {
        // Help text and loading feedback for legal and compliance settings.
        withdrawalNotificationAdmins:
            'Administratoren werden über neu eingereichte Widerrufsformulare benachrichtigt, sofern sie diese Benachrichtigungen nicht in ihren Benachrichtigungseinstellungen deaktiviert haben.',
        settingsPurchaseCancellationPolicy:
            'Dieser Text erscheint auf der Seite für die Widerrufsbelehrung. Erkläre hier, wie Käufer einen Kauf widerrufen können und wohin sie ihren Widerruf senden sollen. Wenn du die Widerrufsbelehrung aktivierst, wird sie in der öffentlichen Fußleiste und unter Mehr in der Mitgliedernavigation verlinkt. Aktiviere das Widerrufsformular separat, damit Käufer ihren Widerruf online einreichen können.',
        settingsPurchaseWithdrawalForm:
            'Über das öffentliche Widerrufsformular können Personen einen Widerruf für einen Kauf einreichen. Wenn das Widerrufsformular aktiviert ist, wird es im öffentlichen Footer unter @:labels.contractWithdrawal verlinkt. Einreichende bestätigen ihre E-Mail-Adresse und erhalten eine Eingangsbestätigung; die Community-Administratoren bearbeiten den Widerruf manuell.',
        agreementLoadFailed: 'Die Vereinbarung konnte nicht geladen werden.',
        accessIdentityLabel: 'Zugang #{id}',
        accessKeyLabel: 'Schlüssel: {name}',
        contactAdminForPaidTickets:
            'Kostenpflichtige Tickets können nur von Admins eingerichtet werden. Wenn du für dieses Event Geld verlangen möchtest, wende dich an einen Admin.',
        nonAdminFreeTicketsOnly:
            'Du kannst hier nur kostenlose Tickets erstellen. Wenn dieses Event kostenpflichtige Tickets braucht, wende dich bitte an einen Admin.',
        accountCannotBeDeleted: 'Dieses Konto kann nicht gelöscht werden, da es Admin dieser Plattform ist.',
        accountDetailsUpdated: 'Kontodaten geändert',
        accountDetailsUpdateFailed: 'Kontodaten konnten nicht aktualisiert werden.',
        shareInMessengerFailed: 'Teilen in Nachrichten fehlgeschlagen.',
        shareInMessengerSuccess: 'In Nachrichten geteilt.',
        accountexists: 'Hast du schon einen Account?',
        adminonlyfeature:
            'Dieses Feature ist nur für Administratoren verfügbar. Bitte kontaktiere deinen Administrator, wenn du es nutzen möchtest.',
        apikeynote: 'HINWEIS: Der Schlüssel wird nicht erneut angezeigt.',
        applicationFeeDefault: 'coapp berechnet eine Plattformgebühr gemäß deinem Abonnement.',
        applicationFeePercent: 'coapp berechnet eine Plattformgebühr von {percentage}% auf alle verarbeiteten Umsätze.',
        approvalDomainHint:
            'Optional: E-Mail-Adressen mit der folgenden Domain werden automatisch genehmigt, nachdem sie ihre E-Mail-Adresse bestätigt haben.',
        auth: {
            accountNotFound: 'Es gibt kein Konto mit der E-Mail-Adresse {email} in dieser Community.',
            accountNotFoundTitle: 'Konto nicht gefunden',
            additionalVerificationRequired:
                'Für dieses Konto ist ein zusätzlicher Verifizierungsschritt erforderlich, bevor der Login abgeschlossen werden kann.',
            codeSentInfo: 'Wir haben einen Bestätigungscode an {email} gesendet.',
            continueWithEmailCode: 'Mit E-Mail-Code fortfahren',
            continueWithPassword: 'Mit Passwort fortfahren',
            expiredOrInvalidSession: 'Die Login-Sitzung ist ungültig oder abgelaufen.',
            expiredOrInvalidInput: 'Die eingegebenen Login-Informationen sind ungültig oder abgelaufen.',
            invalidCredentials:
                'Diese Kombination aus E-Mail und Passwort existiert nicht. Bitte überprüfe die Eingaben und versuche es erneut.',
            invalidInput: 'Bitte überprüfe deine Eingaben und versuche es erneut.',
            invalidOtp: 'Der Bestätigungscode ist ungültig oder abgelaufen.',
            loggingOut: 'Du wirst abgemeldet und die aktuelle Sitzung wird beendet.',
            otpRequired: 'Gib den Bestätigungscode ein, um fortzufahren.',
            passwordPrompt: 'Gib das Passwort für {email} ein, um fortzufahren.',
            resendCode: 'Code erneut senden',
            serviceUnavailable: 'Die Anmeldung ist vorübergehend nicht verfügbar. Bitte versuche es erneut.',
            sendCode: 'Code senden',
            unauthenticated: 'Bitte logge dich ein, um fortzufahren.',
            unexpectedError: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
            useDifferentEmail: 'Eine andere E-Mail verwenden?',
            useEmailCode: 'Einmalcode verwenden',
            usePassword: 'Stattdessen Passwort verwenden',
            verificationCodeLabel: 'Bestätigungscode',
            verificationCodePlaceholder: 'Code eingeben',
            verifyCode: 'Code bestätigen',
        },
        authIdentityProviders: 'Bestehende Community verbinden',
        authMethodCreateFailed: 'Authentifizierungsmethode konnte nicht erstellt werden',
        authMethodUpdateFailed: 'Authentifizierungsmethode konnte nicht aktualisiert werden',
        authSignUpStrategy: 'Lege fest, wie sich Nutzer in deiner Community registrieren können',
        authurlhelp: 'Endpunkt zum Abrufen des Autorisierungscodes',
        billingAddressUpdated: 'Rechnungsadresse aktualisiert',
        billingAddressUpdateFailed: 'Rechnungsadresse konnte nicht aktualisiert werden. Bitte später erneut versuchen.',
        billingAdminDataLoadFailed: 'Billing-Admin-Daten konnten nicht geladen werden',
        captchaReady: 'Menschliche Verifizierung abgeschlossen.',
        captchaWorking: 'Wir prüfen, ob du ein Mensch bist...',
        humanVerificationError:
            'Die menschliche Verifizierung konnte nicht abgeschlossen werden. Bitte lade die Seite neu und versuche es erneut.',
        maxTicketsReached: 'Du kannst insgesamt bis zu {max} Tickets auswählen.',
        billingAdminTooltip:
            'Der Community-Inhaber ist der Administrator, der die Community erstellt hat und für alle Rechnungen und abrechnungsbezogenen Kommunikationen verantwortlich ist.',
        blockingRuleResourceExists: 'Diese Ressource ist bereits in den Sperrregeln vorhanden',
        blockingRulesHelp:
            'Wenn diese Ressource gebucht wird, werden die folgenden Ressourcen für denselben Zeitraum ebenfalls blockiert.',
        blockingRuleSlotsHelp: 'Anzahl der Slots, die für diese Ressource blockiert werden.',
        booking: {
            adminBookingTitle: '{resource} Buchung',
            adminBookingTitleForUser: '{resource} Buchung für {name}',
            cancelationRefundDetails: `Dies storniert deine Buchung von '{resource_name}' für den {date}.<br>
            - Du erhältst eine Bestätigungs-E-Mail<br>
            - Falls du für diese Buchung bezahlt hast, bekommst du eine vollständige Rückerstattung`,
            cancelationRefundDetailsForAdmin: `Dies storniert die Buchung '{booking_title}' für den {date}.<br>
            - Die betroffene Person wird automatisch über die Stornierung informiert<br>
            - Falls es sich um eine bezahlte Buchung handelt, wird eine Rückerstattung veranlasst<br>
            - Wenn diese Buchung bereits auf einer finalisierten Mitgliedsrechnung enthalten war, erfolgt keine Rückerstattung`,
            cancellationinfo: 'Eine Stornierung weniger als {hours} Stunden vor dem Buchungsbeginn ist nicht möglich.',
            cancelSuccess: 'Die Buchung wurde storniert.',
            cancelWarning: 'Möchtest du die Buchung wirklich stornieren?',
            cancelWarningForAdmin: `'{booking_title}' stornieren?`,
            capacityWarning:
                'Die Ressourcenkapazität für das ausgewählte Zeitfenster ist entweder voll oder ausserhalb der verfügbaren Zeiten. Möchtest du trotzdem fortfahren?',
            capacityWarningConfirmation: 'Ja, ich verstehe und möchte fortfahren.',
            error: 'Leider ist beim Erstellen deiner Buchung etwas schiefgelaufen.',
            errorPastTime: 'Du kannst keine Buchung in der Vergangenheit erstellen.',
            lockActionLockTitle: '{lock_name} schließen. Verfügbar von {available_from} bis {available_until}.',
            lockActionLockFailed: 'Dieses Buchungsschloss konnte nicht geschlossen werden.',
            lockActionLockSuccess: 'Der Schließbefehl wurde erfolgreich gesendet.',
            lockActionUnlockTitle: '{lock_name} öffnen. Verfügbar von {available_from} bis {available_until}.',
            lockActionUnlockFailed: 'Dieses Buchungsschloss konnte nicht geöffnet werden.',
            lockActionUnlockSuccess: 'Der Öffnungsbefehl wurde erfolgreich gesendet.',
            lockAvailability: 'PINs und Schlosssteuerungen sind von {available_from} bis {available_until} verfügbar.',
            lockAvailabilityActiveUntil: 'Du kannst die Schlösser bis {available_until} öffnen.',
            lockAvailabilityCountdown:
                'Du kannst die verbundenen Schlösser in {time_until_available} öffnen ({available_from}).',
            lockAvailabilityExpired: 'Die Schlösser können nicht mehr verwendet werden.',
            lockAvailabilityStartsAt: 'Du kannst die verbundenen Schlösser ab {available_from} öffnen.',
            lockAvailabilityUnitHour: '{count} Stunde | {count} Stunden',
            lockAvailabilityUnitMinute: '{count} Minute | {count} Minuten',
            lockAvailabilityUnitSecond: '{count} Sekunde | {count} Sekunden',
            lockPinsRequestFailed: 'Die Buchungs-PINs konnten nicht angefordert werden.',
            lockPinsRequested: 'Die Buchungs-PINs wurden erfolgreich angefordert.',
            lockPinsRequestTitle:
                'PIN für {lock_name} anfordern. Verfügbar von {available_from} bis {available_until}.',
            nobookings: 'Du hast keine zukünftigen Buchungen. Erstelle einfach eine neue!',
            processing: 'Deine Buchung wird verarbeitet.',
            startBeforeEnd: 'Die Startzeit muss vor der Endzeit liegen.',
            success: 'Deine Buchung war erfolgreich.',
            timeRange: 'Die Buchungszeiten für diesen Tag sind von {from} bis {to}.',
        },
        bookingCategoryCreated: 'Buchungskategorie erstellt',
        bookingCategoryCreateFailed: 'Buchungskategorie konnte nicht erstellt werden',
        bookingCategoryDeleted: 'Buchungskategorie gelöscht',
        bookingCategoryDeleteFailed: 'Buchungskategorie konnte nicht gelöscht werden',
        bookingCategoryUpdated: 'Buchungskategorie aktualisiert',
        bookingCategoryUpdateFailed: 'Buchungskategorie konnte nicht aktualisiert werden',
        bookingEmailPinPlaceholder: 'Nutzer-Platzhalter für verknüpften Schloss-PIN:',
        bookingEmailPlaceholdersIntro: 'Platzhalter',
        bookingEmailPlaceholdersOutro: 'werden durch die tatsächlichen Buchungszeiten ersetzt.',
        bookingEmailPlaceholdersTo: 'und',
        bookingNotifyEmailsHelp: 'An diese E-Mails werden Buchungsinformationen und Kontaktdaten gesendet.',
        bookingSystemDisabled:
            'Das Buchungssystem ist derzeit deaktiviert. Bitte aktiviere es in den Einstellungen, damit es von deinen Mitgliedern genutzt werden kann.',
        bookWholeSlotHelp:
            'Wenn aktiviert, werden Min- und Max-Dauer ignoriert und die Ressource für den gesamten verfügbaren Slot des Tages gebucht.',
        allowStartedTimeSlotBookingHelp:
            'Wenn aktiviert, können Mitglieder auch nach dem Startzeitpunkt noch buchen. Beispiel: Wenn der Check-in um 11:00 beginnt und es jetzt 13:30 ist, kann die Buchung trotzdem noch mit Startzeit 11:00 erfolgen. Das gilt auch für kürzere Intervalle, zum Beispiel bei 30-Minuten-Buchungsintervallen kann eine Buchung um 14:15 noch mit Startzeit 14:00 erfolgen.',
        builtindomaininfo: 'Dies ist deine integrierte Domain. Deine Community ist immer über diese Domain erreichbar.',
        customwebaddressinfo:
            'Verbinde deine eigene Domain für deine Community. Unser Support-Team hilft dir bei der Einrichtung.',
        bulkCreateKeepTabOpen: 'Bitte lasse diesen Tab geöffnet, während Nutzer und Mitgliedschaften erstellt werden.',
        bulkCreateMaxRows: 'Maximal 100 Zeilen pro Lauf. Lass diesen Tab geöffnet, bis der Prozess abgeschlossen ist.',
        bulkCreateRequiredColumns:
            'Erforderliche Spalten: E-Mail, Vorname, Nachname, Tarif-ID oder Tarifname. Optional: Firma, Extras-IDs, Extras-Namen, Willkommens-E-Mail unterdrücken, Anrede.',
        buyMoreSeats:
            'Brauchst du mehr Mitglieder, zusätzliche Admin-Plätze oder eine individuelle Enterprise-Lösung? Unser Vertriebsteam berät dich gerne. {0}.',
        deactivateCommunityInfo:
            'Wenn du deine Community deaktivieren oder dauerhaft löschen möchtest, {0}. Unser Support-Team hilft dir bei den nächsten Schritten.',
        buyPass: {
            availableImmediately: 'Zeitpass ist sofort verfügbar.',
            overrideInfo: 'Wenn aktiviert, wird dem Mitglied dieser Zeitpass nicht berechnet.',
        },
        calendarLastSync: 'Letzte Synchronisierung',
        calendarSubscribed: 'Coapp ist für Änderungen dieses Kalenders abonniert.',
        calendarSubscriptionExpires: 'Ablaufdatum der Abo-Periode',
        calendarSyncBookingsToCalendar:
            'Alle kommenden Buchungen aus coapp werden in den ausgewählten Google-Kalender kopiert.',
        calendarSyncConnectHelp:
            'Um die Kalendersynchronisierung zu aktivieren, musst du dein Konto mit deinem Kalender verbinden.',
        calendarSyncEventsToCoapp: 'Alle kommenden Ereignisse aus dem Google-Kalender werden in coapp kopiert.',
        calendarSyncSaveChanges:
            'Bitte speichere andere Änderungen an dieser Ressource, bevor du die Synchronisierung mit Google Kalender startest.',
        calendarSyncSaveAndStart:
            'Alle Änderungen werden gespeichert und die initiale Synchronisierung wird gestartet.',
        calendarSyncStartFailed: 'Kalendersynchronisierung konnte nicht gestartet werden.',
        calendarSyncStarted:
            'Synchronisierung gestartet. Abhängig von der Anzahl der Ereignisse kann es einige Minuten dauern. Du kannst den Tab sicher schließen.',
        cancellationPeriodHelp: 'Verhindert Stornierungen innerhalb von x Stunden vor Buchungsbeginn.',
        cancelUpcomingPlanDescription:
            'Die geplante Tarifänderung wird abgebrochen, sodass sich das aktuelle Abonnement des Nutzers nicht ändert.',
        capacityHelp: 'Wie viele Nutzer können dies gleichzeitig buchen',
        paymentMethodsAvailabilityStripeNotice:
            'Es müssen Zahlungsmethoden in Stripe aktiviert sein, bevor du sie hier einschalten kannst.',
        cf: {
            createdSuccessfully: 'Benutzerdefiniertes Feld erfolgreich erstellt.',
            creationFailed: 'Benutzerdefiniertes Feld konnte nicht erstellt werden.',
            deletedSuccessfully: 'Benutzerdefiniertes Feld erfolgreich gelöscht.',
            deletewarning:
                'Das Löschen dieses benutzerdefinierten Feldes entfernt auch alle zugehörigen Daten aus den Mitgliederprofilen. Diese Aktion kann nicht rückgängig gemacht werden. Bitte sei sicher.',
            updatedSuccessfully: 'Benutzerdefiniertes Feld erfolgreich aktualisiert.',
            updateFailed: 'Benutzerdefiniertes Feld konnte nicht aktualisiert werden.',
        },
        changesApplyImmediately: 'Die Änderungen werden sofort angewendet.',
        changessaved: '@:labels.changes @.lower:labels.saved',
        channel: {
            saved: 'Kanal gespeichert',
            saveError: 'Fehler beim Speichern des Kanalnamens',
        },
        chat: {
            groupnameempty: 'Gruppenname darf nicht leer sein.',
            leavechatconfirm: 'Möchtest du den Chat wirklich verlassen?',
            nomembers: 'Keine Mitglieder in diesem Chat.',
        },
        clientidhelp: 'Client-ID des Providers',
        clientsecrethelp: 'Client-Secret des Providers',
        closedForBookingsHelp: 'Wenn aktiviert, sind Buchungen an diesem Datum nicht möglich.',
        cofile: {
            uploadFailed:
                'Datei konnte nicht hochgeladen werden. Bitte versuche es später erneut. Maximale Dateigröße 500 MB.',
            uploadTooLarge: 'Die Datei ist zu groß. Maximal 500 MB.',
        },
        communityNameHelp: 'Der Name deiner Community',
        communitySuspendedTitle: 'Deine Community wurde gesperrt',
        configureaccesspolicies: 'Zugriffsrichtlinien für den Space konfigurieren (Nutzergruppen)',
        confirmationcodeinfo:
            'Keinen Code erhalten? Überprüfe deinen Spam-Ordner oder versuche es mit einer anderen E-Mail-Adresse.',
        confirmationcodesentotemail: 'Bestätigungscode eingeben, den wir an {email} geschickt haben',
        confirmStatusChange: "Möchtest du den Status des Nutzers wirklich auf '{status}' ändern?",
        congratulations: 'Herzlichen Glückwunsch',
        connectauthprovider: 'Verbinde einen Provider für die Authentifizierung',
        connectionFailed: 'Verbindung fehlgeschlagen',
        connectionSuccessful: 'Verbindung erfolgreich',
        connectionTestFailed: 'Verbindungstest fehlgeschlagen',
        connectStripe: 'Verbinde Stripe, um Zahlungen zu akzeptieren',
        contactBillingManager: 'Kontaktiere deinen Billing-Manager {name}',
        cookiebanner:
            'Wir verwenden Cookies und ähnliche Speichertechnologien, die für die Funktionen dieser Anwendung erforderlich sind. {privacypolicy} ansehen.',
        copiedtoclipboard: 'In die Zwischenablage kopiert',
        copyright: '© 2025 coapp GmbH. Alle Rechte vorbehalten.',
        copyshareablelink: 'Teilbaren Link kopieren',
        countriesLoadFailed: 'Länder konnten nicht geladen werden',
        createApiKeyConfirm: 'Möchtest du wirklich einen neuen API-Schlüssel erstellen?',
        creatingapikey: 'API-Schlüssel wird erstellt....',
        currencyDescription:
            'Lege die Währung fest, die für die Preisgestaltung von Tarifen, Upgrades und Ressourcen verwendet wird.',
        currencyForPlan: 'Währung für Tarif',
        currencyForTimepass: 'Währung für den Zeitpass',
        currencyListLoadFailed: 'Währungsliste konnte nicht geladen werden.',
        currencySettingsLoadFailed: 'Space-Währungseinstellungen konnten nicht geladen werden.',
        customerNumbersBackfilled: 'Kundennummern wurden erfolgreich nachgetragen',
        customerNumbersBackfillFailed: 'Kundennummern konnten nicht nachgetragen werden',
        customerPortalUrlFailed: 'Customer-Portal-URL konnte nicht erstellt werden. Bitte Support kontaktieren.',
        customerPortalUrlMissing: 'Keine gültige Customer-Portal-URL gefunden. Bitte Support kontaktieren.',
        billingStatusLoadFailed: 'Billing-Status konnte nicht geladen werden. Bitte Support kontaktieren.',
        defaultplanused: 'Standardplan wird verwendet: {name}',
        deleteApiKeyWarning:
            'Wenn du diesen API-Schlüssel löschst, kannst du ihn in deiner Anwendung nicht mehr verwenden.',
        deleteEventConfirm: "Event '{title}' löschen?",
        deleteEventWarning: 'Wenn du ein Event löschst, gibt es kein Zurück. Bitte sei dir sicher.',
        deleteImageHeaderWarning: 'Möchtest du das Titelbild wirklich löschen?',
        deleteResourceWarning: 'Sobald du eine Ressource löschst, gibt es kein Zurück. Bitte sei dir sicher.',
        deleteTimepassWarning: 'Wenn du diesen Zeitpass löschst, gibt es kein Zurück. Bitte sei dir sicher.',
        deleteUserConfirm: 'Möchtest du {name} wirklich löschen?',
        deletewarning: 'Das Löschen kann nicht rückgängig gemacht werden. Bitte sei also sicher.',
        deletewarningchannel:
            'Das Löschen eines Kanals kann nicht rückgängig gemacht werden. Möchtest du den Kanal wirklich löschen?',
        domainalreadytaken: 'Diese Domain ist bereits vergeben.',
        domainnamerules:
            'Ein Domainname kann bis zu 63 Zeichen (Buchstaben, Zahlen oder Kombinationen) lang sein.<br />Das einzige Sonderzeichen ist ein Bindestrich (-); der Domainname darf nicht mit einem Bindestrich beginnen oder enden und keine doppelten Bindestriche enthalten.',
        downloadListCsv: 'Liste als CSV-Datei herunterladen',
        durationMustBeSet: 'Du musst eine Dauer festlegen.',
        emailconfirmation: {
            confirmationsuccess: 'E-Mail-Bestätigung erfolgreich!',
            gotocommunity: 'Zur Community',
            invalidcode: 'Der Bestätigungscode ist ungültig oder abgelaufen.',
            wasverified: 'Deine E-Mail wurde verifiziert.',
        },
        emailFooterHelp:
            'Dieser Text wird in die Fußzeile aller E-Mails eingefügt, die von deiner Community gesendet werden.',
        enterconfirmationcode: 'Bestätigungscode eingeben',
        event: {
            attendanceCancellation:
                'Bist du dir sicher, dass du das willst? Diese Veranstaltung wäre viel schöner mit dir!',
            deleteWithParticipants:
                'Diese Veranstaltung hat Teilnehmende und kann nicht gelöscht werden. Du kannst sie stattdessen auf Entwurf setzen.',
            ticketPurchaseSuccess: 'Dein Ticketkauf war erfolgreich.',
            ticketRefund: 'Stornieren',
            ticketRefundTitle: 'Ticket stornieren und Rückerstattung beantragen',
            billingAddressInfo:
                'Diese Rechnungsadresse wird für alle zukünftigen Rechnungen verwendet. Die Adresse kann jederzeit geändert werden.',
            linkvisibleforattendees: 'Link nur für Teilnehmende sichtbar',
            ticketIsNotRefundable: 'Dieses Ticket ist nicht storierbar.',
            ticketRefundDescription:
                'Die Tickets werden sofort storniert. Die Rückerstattung für bezahlte Tickets kann bis zu 5–7 Werktage dauern.',
            ticketRefundError: 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
            ticketRefundSuccess: 'Dein Ticket wurde erfolgreich storniert.',
            externalregistrationrequired: 'Für diese Veranstaltung ist eine externe Registrierung erforderlich.',
        },
        eventcalendarintegrationpaywall:
            'Die The Events Calendar-Integration ist in kostenpflichtigen Plänen verfügbar. Upgrade, um deine Events mit WordPress zu synchronisieren.',
        eventcalendarloadfailed: 'The Events Calendar-Einstellungen konnten nicht geladen werden',
        eventcalendarsyncenabled: 'Aktivieren, um coapp-Events mit WordPress zu synchronisieren.',
        eventCreateFailed: 'Event konnte nicht erstellt werden',
        eventSystemDisabled:
            'Veranstaltungen sind derzeit deaktiviert. Bitte aktiviere sie in den Einstellungen, damit Mitglieder wieder Veranstaltungen erstellen und aufrufen können.',
        pagesSystemDisabled:
            'Seiten und Kanäle sind derzeit deaktiviert. Bitte aktiviere sie in den Einstellungen, damit Mitglieder wieder Seiten erstellen und aufrufen können.',
        failedGeneric: 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        faviconSizeHint: 'Favicon sollte 32x32 Pixel groß sein',
        featuresBookings:
            'Aktiviere Buchungen, um Räume, Equipment, Services oder Coachings direkt in deiner Community buchbar zu machen. Du bestimmst, für welche Tarife Angebote verfügbar sind und ob sie kostenlos oder kostenpflichtig gebucht werden können.',
        featuresPages:
            'Aktiviere Kanäle & Seiten für Gruppen, Zusammenarbeit, Austausch und Wissen. Organisiere Seiten in Kanälen und bestimme, wer sie erstellen, sehen oder ihnen beitreten darf. So entstehen offene Bereiche, private Gruppen oder exklusive Inhalte.',
        featuresEvents:
            'Aktiviere Veranstaltungen, um Events direkt in deiner Community zu organisieren. Du entscheidest, ob sie nur für Mitglieder oder öffentlich zugänglich sind, und wickelst Tickets und Zahlungen direkt ab. Der Event-Feed bietet Raum für Updates, Austausch und Umfragen.',
        featuresHelpLink: 'Mehr über {feature} erfahren',
        featuresMatching:
            'Aktiviere Matchmaking, um Mitglieder nach eigenen Kriterien zu vernetzen. Definiere über benutzerdefinierte Felder, was ein gutes Match ausmacht. Ein Live-Score zeigt passende Kontakte – ohne externe KI-Modelle und mit voller Kontrolle über deine Daten.',
        featuresMarket:
            'Biete Mitgliedern einen gemeinsamen Marktplatz, auf dem sie Community-Angebote veröffentlichen, entdecken und darauf reagieren können.',
        featuresMarketDisableTitle: 'Markt deaktivieren?',
        featuresMarketDisableWarning:
            'Die Markt-Funktion wurde eingestellt. Wenn du sie deaktivierst, wird sie aus deiner Community entfernt und kann nicht wieder aktiviert werden. Diese Aktion kann nicht rückgängig gemacht werden.',
        fileTooLarge: 'Die Datei ist zu groß. Maximal {size}.',
        finalPriceForCustomer: 'Endpreis für den Kunden beträgt',
        giveitaname: 'Gib ihm einen Namen',
        googleCalendarAuthUrlError: 'Fehler beim Abrufen der Google-Kalender-Auth-URL.',
        guest: {
            notamemberyetbody:
                'Als Gast kannst du nur dieses Event sehen. Werde jetzt Mitglied und entdecke, was deine Community sonst noch bereithält.',
            notamemberyettitle: 'Noch kein Mitglied?',
            notmemberyetconfirmtitle: 'Jetzt Mitglied werden',
            notmemberyetdiscardtitle: 'Auf Seite bleiben',
        },
        helpCenterSuffix: 'in unserem Help Center.',
        helpUrlDescription:
            'Definiere eine URL, unter der deine Nutzer Hilfeartikel finden oder den Support kontaktieren können. Das kann eine externe URL oder eine Seite bzw. ein Kanal in deiner Community sein. Der Link zu dieser URL erscheint als "Hilfe"-Eintrag im Dropdown-Menü, wenn Nutzer auf ihr Profilbild klicken.',
        hiddenResourceHelp:
            'Wenn aktiviert, ist diese Ressource für Nutzer nicht sichtbar. Nur Admins können sie sehen.',
        hideeventdesc: 'Importierte Events bleiben verborgen, bis du sie veröffentlichst.',
        hideupcomingevents: 'Importierte Events in Upcoming-Widgets ausblenden.',
        imageguidelines:
            'Bitte vermeide Text im Bild. Das empfohlene Seitenverhältnis ist 3:2 und die empfohlene Bildgröße 1800x1200 Pixel.',
        imageMaxSize2mb: 'Bild, max. Dateigröße 2 MB.',
        imageUploadError: 'Fehler beim Hochladen des Bildes',
        imageUploadFailed: 'Ein oder mehrere Bilder konnten nicht hochgeladen werden. Bitte erneut versuchen.',
        imageUploadSizeError: 'Das Bild ist zu groß. Bitte wähle ein Bild mit einer maximalen Größe von 10 MB.',
        inminutes: 'In Minuten',
        integrationsEventCalendar: 'coapp-Events mit WordPress synchronisieren',
        integrationsGoogleCalendar: 'Buchungsressourcen verbinden und synchronisieren',
        integrationsNuki: 'Nuki-Konto verbinden',
        integrationsZapier: 'coapp-Community mit Zapier verbinden',
        invaliddomain: 'Ungültige Domain.',
        javascriptIntro:
            'Dein benutzerdefiniertes JavaScript wird in den <code>&lt;head&gt;</code>-Tag auf allen Seiten eingefügt.<br /><small>So kannst du Drittanbieter-Software für Kundensupport wie Intercom und FreshDesk, Web-Analytics wie Google Analytics, Matomo und viele weitere Tools nutzen.</small>',
        javascriptNoScriptTags: 'ohne <script> Tags eingeben',
        javascriptPlaceholders:
            'Platzhalter <code v-pre>{{ name }}</code>, <code v-pre>{{ user_id }}</code>, <code v-pre>{{ user_status }}</code> und <code v-pre>{{ email }}</code> können verwendet werden, um Nutzerdaten in deinem Code vorzubefüllen. Alle Platzhalter werden in <code>" "</code> eingefügt, um ihren korrekten Typ (String) in JavaScript darzustellen. Wenn der Nutzer unbekannt ist, z. B. auf der Registrierungsseite, ist der Wert für alle Platzhalter <code>null</code>',
        legal: {
            termsandprivacydisclaimer:
                'Mit der Nutzung dieser Plattform erklärst du dich mit unseren {0} einverstanden und bestätigst, dass du die {1} zur Kenntnis genommen hast.',
        },
        withdrawalEmailHint: 'Gib eine gültige E-Mail-Adresse für die Bestätigung ein.',
        withdrawalFormIntro:
            'Sende deinen Widerruf mit den folgenden Angaben. Wir bestätigen den Eingang automatisch per E-Mail, leiten die Anfrage an die Community-Admins weiter und sie bearbeiten sie manuell.',
        withdrawalRequestError:
            'Dein Widerruf konnte nicht gesendet werden. Bitte prüfe deine Angaben und versuche es erneut.',
        withdrawalRequestSuccess:
            'Dein Widerruf ist eingegangen. Wir senden dir eine Bestätigung an die angegebene E-Mail-Adresse.',
        otpCaptchaExpired:
            'Die menschliche Verifizierung ist abgelaufen. Bitte schließe sie erneut ab, bevor du einen neuen Code anforderst.',
        otpCodeResent: 'Ein neuer Bestätigungscode wurde gesendet.',
        otpRateLimited: 'Zu viele Versuche. Bitte warte einen Moment und versuche es erneut.',
        otpResendFailed: 'Ein neuer Bestätigungscode konnte nicht gesendet werden. Bitte versuche es erneut.',
        otpResending: 'Ein neuer Code wird gesendet…',
        otpVerificationFailed: 'Der Bestätigungscode konnte nicht verifiziert werden. Bitte versuche es erneut.',
        otpVerificationInstructions:
            'Prüfe die E-Mail, die wir an {email} gesendet haben, und gib den Code ein. Prüfe bitte auch deinen Spam-Ordner.',
        otpVerifying: 'Code wird verifiziert…',
        limitAdminsReached:
            'Du hast die maximale Anzahl von Admins in deinem Tarif erreicht. Steige jetzt auf den nächsthöheren Tarif um und füge mehr Admins hinzu.',
        limitUsersAlmostReached:
            'Du hast 80% der maximalen Anzahl von Mitgliedern in deinem Tarif erreicht. Sobald du das Limit erreicht hast, können keine weiteren Mitglieder deiner Community beitreten. Um sicherzustellen, dass sich alle Mitglieder anmelden können, empfehlen wir dir, deinen Tarif upzugraden.',
        limitUsersReached:
            'Du hast die maximale Anzahl von Mitgliedern in deinem Tarif erreicht. Steige jetzt auf den nächsthöheren Tarif um und lade mehr Mitglieder ein.',
        linkmustbevalidurl: 'Link muss eine gültige URL sein',
        audioUnavailable:
            'Dieses Audio ist nicht verfügbar. Es wurde möglicherweise entfernt, privat gestellt oder der Audioanbieter ist vorübergehend nicht erreichbar.',
        audioUrlMustBeSupported:
            'Gib eine gültige Spotify-, Apple-Podcasts-, SoundCloud-, Podigee- oder Mixcloud-URL ein',
        audioUrlSupportedPlatforms:
            'Unterstützte Plattformen: Spotify, Apple Podcasts, SoundCloud, Podigee und Mixcloud.',
        videoUrlMustBeSupported: 'Gib eine gültige YouTube-, Vimeo-, Loom-, TikTok- oder Kinescope-Video-URL ein',
        videoUrlSupportedPlatforms: 'Unterstützte Plattformen: YouTube, Vimeo, Loom, TikTok und Kinescope.',
        videoUnavailable:
            'Dieses Video ist nicht verfügbar. Es wurde möglicherweise entfernt, privat gestellt oder der Videoanbieter ist vorübergehend nicht erreichbar.',
        login: {
            registratonIsNoPossible:
                'Leider sind im Moment keine weiteren Registrierungen möglich. Bitte versuche es zu einem späteren Zeitpunkt erneut oder kontaktiere die Administratoren.',
            registratonIsNoPossibleTitle: 'Keine Registrierungen möglich',
        },
        managespacelocks: 'Space-Schlösser verwalten',
        manageuserkeys: 'Nutzerschlüssel verwalten',
        markasfeatureddesc: 'Neue Events beim Import als Featured markieren.',
        markasstickydesc: 'Events standardmäßig oben anpinnen.',
        marketiterestsubmit: 'Wir werden den Autor über dein Interesse informieren.',
        matching: {
            noMatchesFound:
                'Keine passenden Profile gefunden. Aktualisiere die Profilinformationen, um die Treffer zu verbessern.',
        },
        maxcharacters: 'max. {count} Zeichen',
        memberInvoicesTitle: '{name} – kommende und vergangene Rechnungen',
        memberJoinedCommunity: '{name} ist deiner Community beigetreten',
        membersCsvDownloadTitle: 'Die in dieser Tabelle angezeigten Daten als CSV herunterladen',
        membersCsvExportFailed: 'Der Mitglieder-CSV-Export ist fehlgeschlagen. Bitte versuche es erneut.',
        membersCsvExportProgressInfo:
            'Der Export lädt alle passenden Mitglieder stapelweise und startet den CSV-Download, sobald der letzte Stapel fertig ist.',
        membersCsvExportProgressStatus: '{current} von {total} Mitgliedern vorbereitet',
        membership: {
            subscriptionUpdated: 'Abonnement wurde erfolgreich aktualisiert.',
        },
        membershipCancelFailed: 'Fehler beim Kündigen der Mitgliedschaft',
        membershipCancelled: 'Mitgliedschaft gekündigt',
        membershipCancelScheduled: 'Die Mitgliedschaft von {name} wird am {date} gekündigt',
        membershipStatus: 'Status: {status}',
        membershipStatusCancelled: 'Status: {status}. Gekündigt zum: {date}',
        membersLoadingLonger:
            'In größeren Communities kann es länger dauern, alle Mitglieder zu laden und zu verarbeiten. Bitte habe Geduld. Geladene Seiten: {pagesLoaded}',
        monetizationCollection: {
            subscriptions: {
                cancelationdateinfo:
                    'Benutzer können ihr Abonnement jederzeit sofort auf einen Tarif mit einem höheren Preis pro Abrechnungszeitraum upgraden. Der Preis pro Abrechnungszeitraum wird einschließlich gebuchter Upgrades berechnet.',
            },
        },
        monetizationHelpPrefix: 'Lies unsere',
        monetizationHelpSuffix: 'um mehr darüber zu erfahren, wie du Zahlungen akzeptierst.',
        monetizationPayments: 'Automatisierte Zahlungen konfigurieren',
        monetizationPlans: 'Mitgliedschaften und Preise konfigurieren',
        monetizationTimepasses: 'Zeitpässe für Mitglieder konfigurieren',
        monetizationUpgrades: 'Extras konfigurieren, die in Mitgliedschaften oder Buchungen genutzt werden können',
        moreinfoonourwebsite: 'Für weitere Optionen, öffne die Community in deinem Browser',
        nameRequired: 'Name ist erforderlich',
        noActiveSubscriptionDescription:
            'Deine Community hat kein aktives Abonnement. Bitte abonniere, um coapp weiter zu nutzen.',
        noActiveTimePass: 'Kein aktiver Zeitpass',
        noActivityYet: 'Noch keine Aktivität',
        noBillingAddress: 'Der Nutzer hat noch keine Rechnungsadresse',
        noChannelsYet: 'Du hast noch keine Kanäle. Erstelle welche.',
        noConnectedCalendarAccounts: 'Du hast noch keine verbundenen Kalenderkonten.',
        nodefaultplanselected: 'Kein Standardplan ausgewählt',
        noEventsAttending: 'Du nimmst noch an keiner Veranstaltung teil.',
        noPlanAssigned: 'Kein Tarif zugewiesen',
        noplanavailable: 'Derzeit sind keine Tarife verfügbar.',
        noplanselected: 'Kein Tarif ausgewählt',
        noPlansYet: 'Du hast noch keine Tarife erstellt.',
        noResourcesToBlock: 'Keine Ressourcen zum Blockieren vorhanden.',
        norowsloaded: 'Noch keine Zeilen geladen. CSV hochladen oder Zeile manuell hinzufügen.',
        noTaxRatesYet: 'Du hast noch keine Steuersätze erstellt.',
        noUpcomingPlan: 'Es gibt keinen kommenden Tarif',
        noUpgradesAvailable: 'keine Upgrades verfügbar',
        noUpgradesYet: 'Du hast noch keine Upgrades erstellt.',
        noZapierKeys: 'Du hast noch keine API-Schlüssel. Erstelle einen.',
        noDefaultAvailabilitySet: 'Keine Standardverfügbarkeit festgelegt.',
        nukiAuthUrlError: 'Fehler beim Abrufen der NUKI-Auth-URL.',
        nukiImportDescription: 'Importiere deine Smart Locks 3.0 zur Nutzung in deinen Ressourcen.',
        nukiVerifyFailed: 'NUKI-Konto-Verbindung konnte nicht überprüft werden',
        page: {
            privatePageVisibilityInfo: 'Diese Seite ist nur für Seitenmitglieder und Administratoren sichtbar.',
        },
        pagesLoadFailed: 'Fehler beim Laden der Seiten. Bitte versuche es erneut oder sende einen Bug-Report.',
        passwordlength: 'Passwort darf nicht länger als 56 Zeichen sein',
        passwordResetRequired: 'Passwort-Reset beim nächsten Login erforderlich',
        paymentActivatedBody: 'Um einen Preis zu setzen, musst du mindestens einen öffentlichen Tarif erstellen.',
        paymentActivatedTitle: 'Zahlungen aktiviert:',
        paymentBillingInactivePrefix: 'Zahlung und Abrechnung sind nicht aktiv. Bitte',
        paymentBillingInactiveSuffix: 'um bezahlte Mitgliedspläne und Upgrades zu verwenden.',
        paymentMethodDetached: 'Zahlungsmethode entfernt',
        paymentMethodDetachFailed:
            'Zahlungsmethode konnte nicht entfernt werden. Bitte kontaktiere den Support über das Intercom-Widget.',
        paymentMethodsDescription: 'Verwalte erlaubte Zahlungsmethoden für deine Mitglieder',
        paymentMethodsStripeNotice:
            'Du musst alle Zahlungsmethoden in Stripe aktivieren, bevor du sie hier einschaltest.',
        paymentNotActivatedBody: 'Um einer Ressource einen Preis zu geben, musst du Zahlungen aktivieren.',
        paymentNotActivatedTitle: 'Zahlungen nicht aktiviert:',
        passwordResetAdminInfo:
            'Als Admin kannst du das Passwort für diesen Benutzer zurücksetzen. Der Benutzer erhält eine E-Mail mit Anweisungen zum Setzen eines neuen Passworts.',
        passwordResetAdminLinkSent: 'Passwort-Reset-Link an die E-Mail des Benutzers gesendet.',
        passwordResetAdminLinkSendFailed:
            'Fehler beim Senden des Passwort-Reset-Links. Bitte versuche es erneut oder kontaktiere den Support.',
        permissions: {
            approval: 'Neue Anmeldungen müssen von Admins überprüft und akzeptiert werden, bevor sie beitreten können.',
            inviteonly:
                'Neue Nutzer:innen müssen von Admins eingeladen oder erstellt werden. Nutzer:innen können sich nicht selbst anmelden.',
            open: 'Jede:r kann sich ohne Hilfe eines Admins anmelden. Wenn du Zahlungen aktiviert hast, müssen Nutzer:innen immer einen der Tarife wählen, um sich anmelden zu können.',
        },
        plancheckout: {
            planupdatefailed: 'Die Aktualisierung deines Tarifs ist fehlgeschlagen. Bitte versuche es erneut.',
            planupdatesuccess: 'Dein Tarif wurde erfolgreich aktualisiert.',
        },
        planCreated: 'Tarif erstellt',
        planCreateFailed: 'Tarif konnte nicht erstellt werden',
        planDuplicateCreated: 'Tarif-Duplikat erstellt',
        planDuplicateCreateFailed: 'Tarif-Duplikat konnte nicht erstellt werden',
        planUpdateFailed: 'Tarif konnte nicht bearbeitet werden',
        planUpgradeCreated: 'Tarif-Upgrade erstellt',
        planUpgradeCreateFailed: 'Tarif-Upgrade konnte nicht erstellt werden',
        planUpgradeFetchFailed: 'Tarif-Upgrade konnte nicht geladen werden',
        planUpgradeUpdated: 'Tarif-Upgrade aktualisiert',
        planUpgradeUpdateFailed: 'Tarif-Upgrade konnte nicht aktualisiert werden',
        popupBlocked: 'Bitte erlaube Pop-ups für diese Website.',
        postVisibility: {
            eventAudience: 'Nur Administrator:innen/Organisator:innen und Teilnehmende können diesen Beitrag sehen.',
            feedAndEvent:
                'Dieser Beitrag erscheint im Feed und bei diesem Event. Auf der Eventseite sehen ihn nur Administrator:innen/Organisator:innen und Teilnehmende.',
            feedOnly: 'Dieser Beitrag wird im Feed angezeigt und ist für alle Mitglieder sichtbar.',
            privatePageOnly:
                'Dieser Beitrag wird nur auf dieser privaten Seite angezeigt und ist nur für Seiteninhaber, Administratoren und Mitwirkende sichtbar.',
            privatePageOnlyWithName:
                'Dieser Beitrag wird nur auf der privaten Seite "{pageName}" angezeigt und ist nur für Seiteninhaber, Administratoren und Mitwirkende sichtbar.',
            publicPageFeedAndParent:
                'Dieser Beitrag wird im Feed und auf der öffentlichen Seite "{pageName}" angezeigt und ist für alle Mitglieder und Besucher sichtbar.',
        },
        postDraftRestored: 'Entwurf wiederhergestellt.',
        postDraftSaved: 'Entwurf gespeichert.',
        pricePerSlotHelp:
            'Wenn aktiviert, wird dieser Preis für den gesamten Slot berechnet statt nach gebuchten Stunden.',
        processedRows: 'Verarbeitet {current} von {total}',
        readHelpArticlePrefix: 'Lies unseren',
        readHelpArticleSuffix: 'um zu erfahren, wie die Regeln-Funktion genutzt wird.',
        redirecturlhelp: 'Kopiere diese URL und füge sie in den Einstellungen des Providers ein',
        reduceFeesPrefix: 'Um deine Anwendungsgebühren zu reduzieren,',
        registrationAddressUpdated: 'Registrierungsadresse aktualisiert',
        registrationAddressUpdateFailed:
            'Registrierungsadresse konnte nicht aktualisiert werden. Bitte später erneut versuchen.',
        registrationconfirmationmessage: 'Du findest dein Ticket jederzeit auf der Veranstaltungsseite.',
        removeCobrandingDoItLater: 'Ich mache es später',
        removeCobrandingMsg: 'Wechsele jetzt zu einem kostenpflichtigen Tarif.',
        removeCobrandingTitle: 'Upgraden zum Entfernen des coapp-Brandings',
        resendconfirmationcode: 'Code erneut senden',
        resendconfirmationcodein: 'Code erneut senden in {countdown}',
        resourceCreated: 'Ressource erstellt',
        resourceCreateFailed: 'Ressource konnte nicht erstellt werden',
        resourceDescriptionHelp: 'Kurze Beschreibung und/oder wichtige Hinweise zu dieser Ressource.',
        resourceLocationHelp: 'Wo befindet sich diese Ressource?',
        resourceNameHelp: 'Der Name wird Nutzern auf der Buchungsseite angezeigt.',
        resourceSyncFailed: 'Ressource konnte nicht synchronisiert werden',
        resourceSyncStarted: 'Synchronisierung der Ressource gestartet',
        savetopreview: 'Zum Anzeigen speichern',
        paywallSectionHelp:
            'Mitglieder sehen nur die Inhalte oberhalb dieser Trennlinie. Alles darunter bleibt hinter der Paywall verborgen.',
        scopeshelp: 'Die Scopes müssen durch Kommas getrennt sein',
        searchnomatches: 'Es gibt Keine Treffer für deine Suchanfrage.',
        searchNoResults: 'Keine Ergebnisse für "{term}" gefunden',
        selectConfiguration: 'Wähle deine Konfiguration',
        selectedFile: 'Ausgewählte Datei: {file}',
        sepaContactSupport: 'Um Änderungen an deiner SEPA-Zahlungskonfiguration vorzunehmen,',
        sepaDirectDebitInfo:
            'Möchtest du lieber per SEPA-Lastschrift oder Überweisung zahlen? Möchtest du über Enterprise-Preise oder individuelle Lösungen sprechen? {0}.',
        sepaLimitsNotice:
            'Standardmäßig haben SEPA-Lastschrifttransaktionen ein Limit von 10.000 EUR pro Transaktion. Für neue Kunden gibt es zusätzlich ein wöchentliches Gesamtlmit von 10.000 EUR. Weitere Informationen zu SEPA-Zahlungslimits findest du',
        settingsAccessibility:
            'Dieser Text erscheint auf der Seite für die Barrierefreiheitserklärung. Sie ist in der öffentlichen Fußleiste und unter Mehr in der Mitgliedernavigation verlinkt. Beschreibe hier die Barrierefreiheit deiner Community und wie Personen Barrieren melden oder Unterstützung anfordern können.',
        settingsAccount: 'Abonnement, Zahlungsmethoden und Rechnungen verwalten',
        settingsFeatures: 'Community-Funktionen ein- und ausschalten',
        settingsAuthentication: 'Authentifizierungen von anderen Diensten integrieren',
        settingsCustomization: 'Logo, Farben und Einführungstext einrichten',
        settingsDomain: 'Domainnamen anpassen',
        // Descriptions for the legal and customer settings cards.
        settingsLegalAndCompliance: 'Rechtliche Dokumente und das Widerrufsformular verwalten',
        settingsCustomerNumberSettings: 'Bestehenden Mitgliedern Kundennummern zuweisen',
        settingsGeneral: 'Allgemeine Einstellungen verwalten',
        settingsImprint:
            'Dieser Text erscheint auf der gemeinsamen Seite für Impressum und Datenschutzerklärung. Sie ist in der öffentlichen Fußleiste, auf der Anmeldeseite und in der Mitgliedernavigation verlinkt. Bei der Registrierung müssen neue Mitglieder die Datenschutzerklärung zur Kenntnis nehmen.',
        settingsIntegrations: 'Externe Dienste mit deiner Community verbinden',
        settingsJavaScript: 'Benutzerdefinierte JavaScript-Einbindung',
        settingsPermissions: 'Berechtigungen für deine Community verwalten',
        settingsTerms:
            'Diese Bedingungen erscheinen auf der Seite für Nutzungsbedingungen. Sie ist in der öffentlichen Fußleiste, auf der Anmeldeseite und unter Mehr in der Mitgliedernavigation verlinkt. Mitglieder müssen sie bei der Registrierung, und bei sämtlichen Käufen akzeptieren.',
        signup: {
            accountexists: 'Hast du schon einen Account?',
            chooseyourplan: 'Wähle deinen Tarif aus, um die Details zu sehen und die Registrierung zu starten.',
            jointhecommunity: 'Registrieren',
            noplanavailable: 'Derzeit sind keine Tarife verfügbar.',
        },
        signupV2: {
            email: 'E-Mail eingeben',
            emailSubtitle: 'Gib die E-Mail-Adresse ein, die du verwenden möchtest.',
            termsandprivacydisclaimer: 'Nutzung bedeutet Zustimmung zu {0} & {1}.',
            otp: 'E-Mail bestätigen',
            name: 'Dein Name',
            nameSubtitle: 'Sag uns, wie wir dich ansprechen sollen.',
            nameRequired: 'Gib deinen Vor- und Nachnamen ein, um fortzufahren.',
            password: 'Passwort erstellen',
            passwordSubtitle: 'Wähle ein Passwort mit mindestens 15 Zeichen.',
            passwordRequired: 'Dein Passwort muss mindestens 15 Zeichen lang sein.',
            joinCommunity: 'Community beitreten',
            avatarAlt: 'Profilbild für {name}',
            successTitle: 'Dein Konto wurde erfolgreich erstellt',
            redirectManually: 'Hier klicken, falls du nicht automatisch weitergeleitet wirst',
            waitingForApprovalTitle: 'Registrierung abgeschlossen',
            waitingForApprovalMessage: 'Dein Konto muss noch freigegeben werden.',
            waitingForApprovalEmail: 'Du erhältst eine E-Mail, sobald du loslegen kannst.',
            fieldsRequired: 'Fülle alle Pflichtfelder aus, um fortzufahren.',
            review: 'Registrierung abschließen',
            reviewDescription: 'Prüfe deine Angaben für {email} und akzeptiere die Vereinbarungen.',
            pending: 'Deine Registrierung wartet auf Freigabe.',
            denied: 'Die Registrierung ist für dieses Konto nicht möglich.',
            codeSent: 'Gib den Code ein, der an {email} gesendet wurde.',
            code: 'Bestätigungscode',
            verify: 'Code bestätigen',
            resend: 'Code erneut senden',
            resendIn: 'Code in {seconds}s erneut senden',
            select: 'Option auswählen',
            error: 'Die Registrierung konnte nicht abgeschlossen werden. Bitte versuche es erneut.',
            restart:
                'Wir konnten dein Konto nicht erstellen. Gib deine E-Mail-Adresse erneut ein, um einen neuen Bestätigungscode zu erhalten.',
            rateLimited: 'Zu viele Versuche. Bitte warte {seconds} Sekunden und versuche es erneut.',
            accept: 'Ich akzeptiere die',
            acceptRequired: 'Bitte akzeptiere die erforderlichen Vereinbarungen.',
            accountExists: 'Ein Konto existiert bereits. Bitte melde dich an.',
        },
        signupStrategyApproval:
            'Wähle Genehmigungsbasiert, wenn du Registrierungen vor dem Annehmen oder Ablehnen prüfen möchtest. Mitglieder können weiterhin manuell erstellt oder eingeladen werden. Wenn Zahlungen aktiviert sind, ist diese Strategie nicht verfügbar.',
        signupStrategyApprovalNotice:
            'Deine Registrierungsstrategie ist auf Genehmigung gesetzt. Um Stripe zu verbinden, ändere deine Registrierungsstrategie auf eine andere Option.',
        signupStrategyChangeRequired: 'Bitte ändere deine Registrierungsstrategie, um Stripe zu verbinden',
        signupStrategyInviteOnly:
            'Wähle Nur per Einladung, wenn nur Nutzer beitreten dürfen, die von deinen Admins eingeladen oder hinzugefügt werden. Der öffentliche Registrierungsbereich wird deaktiviert.',
        signupStrategyOpen:
            'Wähle Jeder kann sich registrieren, wenn sich alle ohne Hilfe eines Admins registrieren dürfen. Wenn Zahlungen aktiviert sind, müssen Nutzer immer einen Tarif wählen. Mitglieder können weiterhin manuell erstellt oder eingeladen werden.',
        signupStrategyUpdated: 'Registrierungsstrategie erfolgreich aktualisiert',
        signupStrategyUpdateFailed: 'Registrierungsstrategie konnte nicht aktualisiert werden',
        spaceSettingsLoadFailed: 'Space-Einstellungen konnten nicht geladen werden',
        spaceSettingsRefreshFailed: 'Aktualisierte Space-Einstellungen konnten nicht geladen werden.',
        spaceSettingsSaveFailed: 'Space-Einstellungen konnten nicht gespeichert werden',
        startsAt: 'Startet am: {date}',
        stripeConnectedTitle: 'Stripe ist verbunden und aktiv',
        stripeConnectFailed: 'Stripe-Verbindung fehlgeschlagen. Bitte kontaktiere unser Support-Team.',
        stripeDetachSupport:
            'Wenn du dein Stripe-Konto von coapp trennen möchtest, kontaktiere bitte unser Support-Team.',
        stripeFeesInfo:
            'Stripe erhebt eine zusätzliche Gebühr für "Stripe Billing" sowie Zahlungsgebühren, die je nach Zahlungsmethode variieren. Eine Übersicht der Stripe-Zahlungsgebühren findest du',
        stripeNeedsInfoDescription: 'Deinem Stripe-Konto fehlen Informationen, bevor es aktiviert werden kann.',
        stripeNeedsInfoTitle: 'Stripe benötigt weitere Informationen',
        stripePaymentsIntro: 'coapp nutzt Stripe für Zahlungs- und Abrechnungsdienste für deine Community.',
        stripePendingDescription:
            'Ausstehend bedeutet, dass das Konto von Stripe geprüft oder verifiziert wird. Dies geschieht, wenn Stripe die bereitgestellten Informationen prüft, z. B. ein Ausweisdokument. Das kann einen Moment dauern. Wenn sich dieser Status nicht ändert,',
        stripePendingTitle: 'Stripe-Verbindung steht aus',
        stripeSetupPrompt:
            'Um coapp Payments über Stripe einzurichten, musst du zuerst ein Stripe-Konto erstellen. Bereit loszulegen?',
        stripeStartAcceptingTitle: 'Akzeptiere jetzt Zahlungen',
        strongTagsHint:
            'Nur die ersten 10 Tags werden in der Nutzerliste angezeigt, wähle daher die wichtigsten zuerst.',
        subscriptionCancelledNotice: 'Dein coapp-Abonnement wurde gekündigt. Es endet am',
        subscriptionAccessRestrictedDescription:
            'Der/Die Billing-Manager:in kann das coapp-Abonnement verwalten, den Tarif ändern, Rechnungen öffnen und Zahlungsmethoden aktualisieren.',
        deactivateOrDeleteCommunitySupportMessage:
            'Hallo coapp Support Team, ich möchte meine Community deaktivieren oder löschen.',
        subscriptionManagedByOwner: 'Nur der Billing-Manager kann das coapp-Abonnement verwalten.',
        subscriptionManageDescription:
            'Verwalte dein Abonnement, lade Rechnungen herunter und konfiguriere deine Zahlungsdaten.',
        suspension: {
            community:
                'Diese Community ist derzeit gesperrt. Bitte kontaktiere den Kundendienst, um die Situation zu klären.',
            communityNotAvailable: 'Community nicht verfügbar',
            permanentBan:
                'Diese Community wurde dauerhaft gesperrt und ist nicht mehr verfügbar. Wenn du denkst, dass dies ein Fehler ist, kontaktiere bitte den Kundendienst.',
        },
        taxRateActive: 'Steuersatz ist aktiv',
        taxRateActiveHelp:
            'Standardmäßig auf true gesetzt. Wenn deaktiviert, kann dieser Steuersatz nicht für neue Checkout-Sitzungen verwendet werden, funktioniert aber weiterhin für Abonnements und Rechnungen, die ihn bereits haben.',
        taxRateCreated: 'Steuersatz erstellt',
        taxRateCreateFailed: 'Steuersatz konnte nicht erstellt werden',
        taxRateDescription: 'Definiere Steuersätze für Tarife, Upgrades und Ressourcen',
        taxRateDescriptionHelp: 'Details zum Steuersatz nur für deinen internen Gebrauch. Für Kunden nicht sichtbar.',
        taxRateDisplayNameHelp: "Der Anzeigename des Steuersatzes, der den Nutzern angezeigt wird. z. B. 'MwSt.'",
        taxRateLoadFailed: 'Steuersatz konnte nicht geladen werden',
        taxRatePercentHelp: 'Dies entspricht dem Steuersatz in Prozent von 100.',
        taxRateRequired: 'Bitte lege mindestens einen Steuersatz an',
        taxRateRequiredForPlanUpgrade:
            'Bitte lege mindestens einen Steuersatz an, bevor du ein Tarif-Upgrade erstellst',
        taxRatesPrefilled: 'Steuersätze wurden automatisch vorausgefüllt. Du kannst sie ändern.',
        taxRateStripeSetupNotice: 'Bevor du Steuersätze erstellst, musst du zuerst deine Stripe-Verbindung einrichten.',
        taxRateTypeHelp: 'Gibt an, ob der Steuersatz inklusiv oder exklusiv ist.',
        taxRateUpdated: 'Steuersatz aktualisiert',
        taxRateUpdateFailed: 'Steuersatz konnte nicht aktualisiert werden',
        termsAndConditionsHelp:
            'Wenn dieses Feld gesetzt ist, müssen Nutzer die Nutzungsbedingungen vor der Buchung akzeptieren.',
        timepass: {
            applyFailed: 'Zeitpass konnte nicht angewendet werden',
            applyForUserFailed: 'Zeitpass konnte nicht für den Benutzer angewendet werden',
            applyForUserSuccess: 'Zeitpass erfolgreich für den Benutzer angewendet',
            applySuccess: 'Zeitpass erfolgreich angewendet',
            userHasActivePass: 'Benutzer hat bereits einen aktiven Zeitpass',
            youHaveActivePass: 'Du hast bereits einen aktiven Zeitpass',
        },
        timepassAlreadyActive: 'Du hast bereits einen aktiven Zeitpass',
        timepassApplied: 'Zeitpass erfolgreich angewendet',
        timepassApplyFailed: 'Zeitpass konnte nicht angewendet werden',
        timepassCreateFailed: 'Zeitpass konnte nicht erstellt werden',
        timepassDeleted: 'Zeitpass erfolgreich gelöscht',
        timepassUpdateFailed: 'Zeitpass konnte nicht aktualisiert werden',
        timepassUsageDescription: 'Anzahl der Nutzungen, die ein Nutzer mit diesem Zeitpass hat',
        timepassWalletLoadFailed: 'Zeitpässe im Wallet konnten nicht geladen werden',
        tokenurlhelp: 'Endpunkt zum Abrufen des Access-Tokens',
        toUseThisFeatureActivatePayments: 'Um einem Ticket einen Preis zu geben, musst du zuerst Zahlungen aktivieren.',
        unsavedchangeswarning:
            'Du hast ungespeicherte Änderungen. Bist du sicher, dass du die Seite verlassen möchtest?',
        unsavedwarning: 'Deine Änderungen gehen verloren. Bist du sicher, dass du forfahren möchtest?',
        upgradeBarText: 'Mehr Funktionen, größere Community - <strong>Jetzt upgraden</strong>',
        upgradeBarTextMobileApp: 'Eine kostenlose Community, erstellt mit <strong>coapp</strong>',
        upgradesSetupPaymentsNotice:
            'Bevor du kostenpflichtige Tarife und Upgrades erstellst, musst du Zahlungen über Stripe einrichten und mindestens einen Steuersatz anlegen.',
        uploadImageFailed: 'Bild konnte nicht hochgeladen werden.',
        uploadingimages: 'Bilder werden hochgeladen',
        useapikey: 'Nutze diesen Schlüssel, um den Zugriff auf deinen Space durch Zapier zu autorisieren',
        userApprovalFailed: 'Benutzerfreigabe fehlgeschlagen',
        userApproved: 'Benutzer freigegeben',
        userDataLoadFailed: 'Benutzerdaten konnten nicht geladen werden',
        userDeleted: 'Benutzer gelöscht',
        userDeleteFailed: 'Benutzer konnte nicht gelöscht werden',
        userHasNoPaymentMethods: '{name} hat keine Zahlungsmethoden',
        userinfourlhelp: 'Endpunkt zum Abrufen von Benutzerinformationen',
        userMigrated: 'Nutzer wurde zu coapp migriert',
        usernotexists: 'User existiert nicht oder wurde gelöscht.',
        userRejected: 'Benutzer abgelehnt',
        userRejectionFailed: 'Benutzerablehnung fehlgeschlagen',
        userstatus: {
            3: 'Dein Konto ist derzeit gesperrt. Dies ist wahrscheinlich auf ein Zahlungsproblem zurückzuführen. Bitte kontaktiere den Kundenservice, um die Situation zu klären.',
            4: 'Dein Konto ist derzeit gekündigt. Bitte abonniere einen Tarif, um deine Mitgliedschaft zu reaktivieren.',
            5: 'Dein Konto wartet derzeit auf die Freigabe durch einen Admin. Sobald die Freigabe für dein Konto erteilt wurde, erhältst du eine E-Mail. Bitte stelle sicher, dass du deine E-Mail-Adresse bestätigt hast. Hierzu hast du bereits eine separate E-Mail erhalten.',
            7: "Dein Konto hat den Gaststatus. Als Gast kannst Du 'Meine Events' aufrufen und öffentliche Events ansehen. Der Rest der Community ist nicht verfügbar. Kontaktiere das Team, um Deinen Status zu ändern.",
            7.2: "Dein Konto hat den Gaststatus. Als Gast kannst Du 'Meine Events' aufrufen und öffentliche Events ansehen. Der Rest der Community ist nicht verfügbar. Buche einen Mitgliedsplan im Bereich 'Mitgliedschaft', um Dein Konto zu upgraden.",
        },
        visibilitysyncnotice: 'Nur Events mit diesen Sichtbarkeiten werden synchronisiert.',
        zapierInviteNotice:
            'Die coapp.io-Integration mit Zapier ist noch nicht öffentlich. Klicke auf den Einladungslink, um die Integration zu nutzen.',
    },
    meta_title: {
        createYourCommunity: 'Erstelle deine Community',
    },
    notFound: {
        actions: {
            back: 'Zurück',
            primary: {
                // notused
                event: 'Alle Veranstaltungen',
                generic: 'Zum Feed',
                marketItem: 'Zum Marktplatz',
                page: 'Zu den Seiten',
                post: 'Zum Feed',
                user: 'Zur Community',
            },
        },
        code: 'FEHLER 404',
        messages: {
            event: 'Die gesuchte Veranstaltung wurde gelöscht, hat nie existiert, der Link wurde geändert oder dir fehlt die Berechtigung.',
            generic:
                'Der gesuchte Inhalt wurde gelöscht, hat nie existiert, der Link wurde geändert oder dir fehlt die Berechtigung.',
            marketItem:
                'Der gesuchte Marktplatzartikel wurde gelöscht, hat nie existiert, der Link wurde geändert oder dir fehlt die Berechtigung.',
            page: 'Die gesuchte Seite wurde gelöscht, hat nie existiert, der Link wurde geändert oder dir fehlt die Berechtigung.',
            post: 'Der gesuchte Beitrag wurde gelöscht, hat nie existiert, der Link wurde geändert oder dir fehlt die Berechtigung.',
            user: 'Das gesuchte Mitglied wurde gelöscht, hat nie existiert, der Link wurde geändert oder dir fehlt die Berechtigung.',
        },
        titles: {
            event: 'VERANSTALTUNG NICHT GEFUNDEN',
            generic: 'INHALT NICHT GEFUNDEN',
            marketItem: 'MARKTPLATZ-EINTRAG NICHT GEFUNDEN',
            page: 'SEITE NICHT GEFUNDEN',
            post: 'BEITRAG NICHT GEFUNDEN',
            user: 'MITGLIED NICHT GEFUNDEN',
        },
    },
    page: {
        private: 'Private Seite',
        usecaselabel: 'Wie möchtest du diese Seite verwenden?',
        privateDescription:
            'Die Seite ist privat und nur für Mitglieder der Seite sichtbar. Sie wird nicht im Kanal aufgeführt und ist für Nicht-Mitglieder nicht auffindbar. Nur Eigentümer können Mitglieder manuell zur Seite hinzufügen. Dies ist die beste Option, wenn du eine Seite für ein bestimmtes Projekt oder Thema erstellen möchtest und nur einer begrenzten Anzahl von Personen Zugriff gewähren möchtest.',
        community: 'Community-Seite',
        communityDescription:
            'Die Seite ist für alle Mitglieder der Community sichtbar und im Kanal aufgeführt. Dies ist die beste Option, wenn du eine Seite für ein bestimmtes Projekt oder Thema erstellen möchtest und allen Mitgliedern der Community Zugriff gewähren möchtest.',
        paid: 'Seite mit exklusiven Inhalten',
        paidDescription:
            'Seiten mit exklusiven Inhalten sind für alle Mitglieder sichtbar und auffindbar. Lege fest, ab welchem Abschnitt Inhalte nur noch für Mitglieder mit einem bestimmten Tarif verfügbar sind. Alles oberhalb bleibt für alle sichtbar, alles darunter kann über Freischalten mit dem passenden Tarif aufgerufen werden. Ideal für Kurse, Mitgliederbereiche und exklusive Inhalte.',
        paidmembership: 'Seite mit exklusiven Inhalten',
        exclusive: 'Exklusive',
    },
    paywall: {
        cta: 'Jetzt upgraden',
        cta2: 'Angebot anfordern',
        featureCopy: {
            // Permission management starts with the Professional plan.
            permissions: {
                free: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Lege fest, wer Inhalte erstellen kann',
                    subline: 'Wechsel jetzt zum Professional-Tarif für 99 € im Monat, um Berechtigungen zu verwalten.',
                },
                starter: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Lege fest, wer Inhalte erstellen kann',
                    subline: 'Wechsel jetzt zum Professional-Tarif für 99 € im Monat, um Berechtigungen zu verwalten.',
                },
                unknown: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Lege fest, wer Inhalte erstellen kann',
                    subline: 'Wechsel jetzt zum Professional-Tarif für 99 € im Monat, um Berechtigungen zu verwalten.',
                },
            },
            'announce-post': {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Alle Mitglieder über wichtige Updates informieren',
                    subline: 'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, um Beiträge anzukündigen.',
                },
                unknown: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Alle Mitglieder über wichtige Updates informieren',
                    subline: 'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, um Beiträge anzukündigen.',
                },
            },
            'bigger-files': {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Größere Datei-Uploads ermöglichen',
                    subline:
                        'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, um Dateien mit einer Größe von bis zu 25 MB hochzuladen.',
                },
                starter: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Größere Datei-Uploads ermöglichen',
                    subline:
                        'Wechsel jetzt zum Professional-Tarif für 99 € im Monat, um Dateien mit einer Größe von bis zu 100 MB hochzuladen.',
                },
                professional: {
                    bullets: [
                        '1.000 Mitglieder',
                        '5 Admins',
                        'Nur noch 2% Plattformgebühren',
                        'Priority Support',
                        'White-Label-App (€399)',
                    ],
                    headline: 'Größere Datei-Uploads ermöglichen',
                    subline:
                        'Wechsel jetzt zum Business-Tarif für 399 € im Monat, um Dateien mit einer Größe von bis zu 500 MB hochzuladen.',
                },
                business: {
                    bullets: ['Individuelles Angebot', 'Bevorzugter Support', '1000+ Mitglieder'],
                    headline: 'Größere Datei-Uploads ermöglichen',
                    subline:
                        'Upload-Limit erreicht? Für Dateien über 500 MB erstellen wir dir im Enterprise-Tarif das passende Paket.',
                },
                unknown: {
                    bullets: [
                        'Die Dateigröße pro Upload hängt von deinem Tarif ab',
                        'Starter: 25 MB, Professional: 100 MB, Business: 500 MB',
                        'Hochauflösende Medien teilen',
                        'Größere Dokumente mühelos hochladen',
                    ],
                    headline: 'Größere Datei-Uploads ermöglichen',
                    subline: 'Upgrade, um die Upload-Limits zu erhöhen.',
                },
            },
            'custom-fields': {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Mehr benutzerdefinierte Felder für deine Profile',
                    subline:
                        'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, um unbegrenzt viele benutzerdefinierte Felder anzulegen.',
                },
                unknown: {
                    bullets: [
                        'Fortgeschrittene Datenstrukturen',
                        'Verwaltung großer Feldmengen',
                        'Aktivierung durch unser Support-Team',
                    ],
                    headline: 'Mehr benutzerdefinierte Felder für deine Profile',
                    subline: 'Kontaktiere bitte unseren Support, um deine Profil-Einstellungen anzupassen.',
                },
            },
            customization: {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Gestalte deine Plattform',
                    subline:
                        'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat und passe das Design ganz nach deinen Wünschen an.',
                },
                unknown: {
                    bullets: [
                        'Logo, Favicon und Farben anpassen',
                        'Verfügbar in Starter, Professional und Business',
                        'Ein gebrandetes Community-Erlebnis schaffen',
                    ],
                    headline: 'Gestalte deine Plattform',
                    subline: 'Upgrade deinen Tarif und passe das Design ganz nach deinen Wünschen an.',
                },
            },
            'branded-domain': {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Eigene Domain verwenden',
                    subline: 'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat und verbinde deine eigene Domain.',
                },
                unknown: {
                    bullets: ['Eigene Domain', 'Support-Unterstützung', 'Professionellerer Auftritt'],
                    headline: 'Eigene Domain verwenden',
                    subline: 'Upgrade deinen Tarif, um deine eigene Domain zu verbinden.',
                },
            },
            'more-admins': {
                free: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Weitere Admins hinzufügen',
                    subline: 'Wechsel jetzt zum Professional-Tarif für 99 € im Monat und füge bis zu 3 Admins hinzu.',
                },
                starter: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Weitere Admins hinzufügen',
                    subline: 'Wechsel jetzt zum Professional-Tarif für 99 € im Monat und füge bis zu 3 Admins hinzu.',
                },
                professional: {
                    bullets: [
                        '1.000 Mitglieder',
                        '5 Admins',
                        'Nur noch 2% Plattformgebühren',
                        'Priority Support',
                        'White-Label-App (€399)',
                    ],
                    headline: 'Weitere Admins hinzufügen',
                    subline: 'Wechsel jetzt zum Business-Tarif für 399 € im Monat und füge bis zu 5 Admins hinzu.',
                },
                business: {
                    bullets: ['Individuelles Angebot', 'Bevorzugter Support', '1000+ Mitglieder'],
                    headline: 'Weitere Admins hinzufügen',
                    subline:
                        'Admins-Limit erreicht? Gerne erstellen wir ein individuelles Enterprise-Angebot mit der gewünschten Anzahl an Admins.',
                },

                unknown: {
                    bullets: ['Mehr Admin-Sitze', 'Teile Verwaltungsaufgaben', 'Limits entfernen'],
                    headline: 'Weitere Admins hinzufügen',
                    subline: 'Wechsel jetzt den Tarif und füge bis zu 5 Admins hinzu.',
                },
            },
            'more-channels': {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Mehr Kanäle erstellen',
                    subline: 'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, um bis zu 5 Kanäle zu erstellen.',
                },
                starter: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Mehr Kanäle erstellen',
                    subline:
                        'Wechsel jetzt zum Professional-Tarif für 99 € im Monat, um unbegrenzt viele Kanäle zu erstellen.',
                },
                unknown: {
                    bullets: [
                        'Organisiere Wissen und Diskussionen',
                        'Sorge für mehr Übersichtlichkeit',
                        'Lege Berechtigungen je Kanal fest',
                    ],
                    headline: 'Mehr Kanäle erstellen',
                    subline:
                        'Du hast das Limit für Kanäle erreicht. Kontaktiere unseren Support, um deine Kapazität zu erhöhen.',
                },
            },
            'more-members': {
                free: {
                    bullets: [
                        '100 Mitglieder',
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Vergrößer deine Community',
                    subline:
                        'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, damit deine Community weiter wachsen kann.',
                },
                starter: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Vergrößer deine Community',
                    subline:
                        'Wechsel jetzt zum Professional-Tarif für 99 € im Monat, damit deine Community weiter wachsen kann.',
                },
                professional: {
                    bullets: [
                        '1.000 Mitglieder',
                        '5 Admins',
                        'Nur noch 2% Plattformgebühren',
                        'Priority Support',
                        'White-Label-App (€399)',
                    ],
                    headline: 'Vergrößer deine Community',
                    subline:
                        'Wechsel jetzt zum Business-Tarif für 399 € im Monat, damit deine Community weiter wachsen kann.',
                },
                business: {
                    bullets: ['Individuelles Angebot', 'Bevorzugter Support', '1000+ Mitglieder'],
                    headline: 'Vergrößer deine Community',
                    subline:
                        'Mitglieder-Limit erreicht? Gerne erstellen wir ein individuelles Enterprise-Angebot mit der gewünschten Anzahl an Mitgliedern.',
                },
                unknown: {
                    bullets: ['Höheres Mitgliederlimit', 'Skaliere deine Community', 'Limits entfernen'],
                    headline: 'Vergrößer deine Community',
                    subline: 'Upgrade, um dein Mitgliederlimit zu erhöhen.',
                },
            },
            'more-storage': {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Erhöhe deine Speicherkapazität',
                    subline:
                        'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat und erhalte 100GB Speicherplatz für deine Dateien.',
                },
                starter: {
                    bullets: [
                        '500 Mitglieder',
                        '3 Admins',
                        'API, Zapier & OAuth2',
                        'Nur noch 5% Plattformgebühren',
                        '200 GB Speicherplatz',
                    ],
                    headline: 'Erhöhe deine Speicherkapazität',
                    subline:
                        'Wechsel jetzt zum Professional-Tarif für 99 € im Monat und erhalte 200GB Speicherplatz für deine Dateien.',
                },
                professional: {
                    bullets: [
                        '1.000 Mitglieder',
                        '5 Admins',
                        'Nur noch 2% Plattformgebühren',
                        'Priority Support',
                        'White-Label-App (€399)',
                    ],
                    headline: 'Erhöhe deine Speicherkapazität',
                    subline:
                        'Wechsel jetzt zum Business-Tarif für 399 € im Monat und erhalte 500GB Speicherplatz für deine Dateien.',
                },
                business: {
                    bullets: ['Individuelles Angebot', 'Bevorzugter Support', '1000+ Mitglieder'],
                    headline: 'Erhöhe deine Speicherkapazität',
                    subline:
                        'Speicherkapazität-Limit erreicht? Gerne erstellen wir ein individuelles Enterprise-Angebot mit der gewünschten Speicherkapazität für deine Dateien.',
                },
                unknown: {
                    bullets: [
                        'Ermögliche das Teilen von Dateien',
                        'Mehr Interaktion dank größerer Speicherkapazität',
                        'Alle Dateien an einem zentralen Ort',
                    ],
                    headline: 'Erhöhe deine Speicherkapazität',
                    subline: 'Upgrade, um deine Speicherkapazität zu erhöhen.',
                },
            },
            'pin-post': {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Beiträge oben fixieren',
                    subline: 'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, um Beiträge anheften zu können.',
                },
                unknown: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Beiträge oben fixieren',
                    subline: 'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, um Beiträge anheften zu können.',
                },
            },
            'white-label': {
                free: {
                    bullets: [
                        'Eigene Domain',
                        'coapp-Branding entfernen',
                        'Unbegrenzt Profilfelder',
                        '100 Mitglieder',
                        '100 GB Speicherplatz',
                    ],
                    headline: 'Entferne das coapp-Branding',
                    subline:
                        'Wechsel jetzt zum Starter-Tarif für nur 19 € im Monat, um das coapp-Branding dauerhaft zu entfernen.',
                },
                unknown: {
                    bullets: [
                        'Verfügbar in Starter, Professional und Business',
                        'coapp-Branding entfernen',
                        'Eine vollständig gebrandete Community bereitstellen',
                    ],
                    headline: 'Entferne das coapp-Branding',
                    subline: 'Upgrade, um das coapp-Branding dauerhaft zu entfernen.',
                },
            },
        },
    },
    placeholders: {
        actionbuttontitle: 'Titel für Aktionsbutton eingeben',
        actionbuttonurl: 'URL/Link für Aktionsbutton eingeben',
        additionalinfo: 'z. B. Abteilung',
        address: 'Straße und Hausnummer',
        addressadditionalinformation: "z. B. 'Wohnung 1'",
        authtokenhelper: 'Falls deine Seite ein Token benötigt',
        autocompleteempty: 'Keine Ergebnisse',
        categoryname: 'Kategoriename eingeben',
        cf: {
            dataType: 'Datentyp auswählen',
            editability: 'Bearbeitbarkeit auswählen',
            identifier: 'Bezeichner eingeben',
            // Platzhalter für benutzerdefinierte Felder
            label: 'Label eingeben',
            optionsValues: 'z. B. Option 1,Option 2,Option 3',
            requestedOnSignUp: 'Registrierungsoption auswählen',
            sortOrder: 'Sortierreihenfolge eingeben',
            visibility: 'Sichtbarkeit auswählen',
        },
        channelnameexample: 'Name (Projekt, StartUp, MeetUp)',
        comment: 'Hinterlasse einen Kommentar',
        communityname: 'Gib deiner Community einen Namen',
        companyname: 'Firmenname',
        couponcode: 'Rabatt-Code eingeben',
        date: 'Wähle ein Datum',
        domainexample: 'z.B. beste-community',
        editorCode: '<html>Füge deinen Code ein ...</html>',
        email: 'mail@beispiel.de',
        emailfooter: 'Footer/Disclaimer-Text hier definieren',
        invoiceNumber: 'z. B. RE-12345',
        enterauthurl: 'Auth-URL eingeben',
        enterclientid: 'Client-ID eingeben',
        enterclientsecret: 'Client-Secret eingeben',
        entername: 'Namen eingeben',
        entertokenurl: 'Token-URL eingeben',
        entertext: 'Text hier eingeben ...',
        enteruserinfourl: 'User-Info-URL eingeben',
        file: 'Keine Datei gewählt',
        filechooseordrop: 'Datei auswählen oder hier ablegen...',
        filedragdropupload: 'Ziehe eine Datei hierher oder klicke',
        filedrop: 'Datei hier ablegen...',
        filteritems: 'Einträge filtern',
        groupname: 'Gib deiner Gruppe einen Namen',
        hexcode: 'Hex-Code',
        image: 'Klicke um das Bild zu ändern',
        imagedragdropupload: 'Ziehe ein Bild hierher oder klicke',
        javascriptcode: 'JavaScript-Code',
        loginctatitle: 'Titel für den Login-Button eingeben',
        marketitemcontact: 'Link zu einer Kontaktseite oder eine E-Mail',
        marketitemdescription:
            'Die Beschreibung deines Artikels wird auf der Detailseite deines Artikels angezeigt und sollte ihn ausführlich beschreiben.',
        marketitemsummary: 'Die Zusammenfassung deines Artikels wird im Feed und in der Marktliste angezeigt.',
        marketitemtitle: 'Wähle einen guten namen :)',
        messagecreate: 'Sende eine Nachricht ...',
        nameresourcegroup: 'Ressourcengruppe benennen',
        newPrice: 'Neuer Preis',
        note: 'Notiz hinzufügen',
        number: 'Nummer',
        optional: 'optional',
        password: 'gib dein @:labels.password ein',
        planmultiselect: 'Tarife auswählen',
        phonenumber: 'Telefonnummer',
        placeholderoptional: 'Platzhalter (optional)',
        post: '@:labels.post @.lower:labels.create',
        postalcode: '12345',
        posteditor: 'Schreibe hier ...',
        registrationlink: 'Öffentlicher Registrierungslink',
        resourcename: 'Ressourcenname eingeben',
        scopeexample: 'Beispiel: scope1,scope2,scope3',
        search: 'Suche',
        searchnameoremail: 'Suche nach Name oder E-Mail',
        searchaccesskey: 'Zugriffsschlüssel suchen',
        searchaccesspolicy: 'Zugriffsrichtlinie suchen',
        searchevent: 'Event suchen',
        searchmember: 'Nach einem Mitglied suchen',
        searchmembers: 'Nach Personen suchen',
        select: '@:placeholders.selectplease',
        selectcalendar: 'Kalender auswählen',
        selectcollectionmethod: 'Zahlungsmethode auswählen',
        selectconnectedaccount: 'Verbundenes Konto auswählen',
        selectcountry: '-- Bitte ein Land auswählen --',
        selectcsvfile: 'CSV-Datei auswählen',
        selectlocation: 'Standort auswählen',
        selectlock: 'Schloss auswählen',
        selectplease: 'Bitte wähle eine Option',
        selectresource: 'Ressource auswählen',
        selectsalutation: 'Anrede auswählen',
        selecttaxrate: 'Wähle den Steuersatz',
        selectTimezone: 'Wähle deine Zeitzone',
        selectvisibilities: 'Sichtbarkeiten auswählen',
        signupctatitle: 'Titel für den Sign-up-Button eingeben',
        spacename: 'Name deines Spaces eingeben',
        strongtagssuggestion: 'Strong-Tags-Vorschlag',
        subtitle: 'Untertitel',
        subtitleoptional: 'Untertitel (optional)',
        supporturl: 'Support-Seiten-URL',
        tags: 'Tippe hier',
        taxratename: 'Steuersatzname eingeben',
        termsandconditions: ' ',
        termsandconditionslink: 'Link zu den Nutzungsbedingungen für diese Ressource eingeben',
        textoptional: 'Text (optional)',
        ticketname: 'e.g. Early Bird',
        ticketurlplaceholder: 'Link zu den Tickets',
        title: 'Titel eingeben',
        typeanything: 'beliebig eingeben',
        typehere: 'Hier eingeben',
        updatecreate: '@:labels.update @.lower:labels.create',
        url: 'https://',
        urlexample: 'https://beispiel.de',
        audioUrl: 'https://open.spotify.com/episode/...',
        videoUrl: 'https://www.youtube.com/watch?v=...',
        vatid: 'DE123456789',
    },
    polls: {
        addoption: 'Option hinzufügen',
        addpoll: 'Umfrage hinzufügen',
        multiselect: 'Mehrfachantworten erlauben',
        optionPlaceholder: 'Option eingeben',
        options: 'Optionen',
        poll: 'Umfrage',
        polls: 'Umfragen',
        pollsettings: 'Umfrage-Einstellungen',
        pollvoteadded: 'Deine Stimme wurde abgegeben',
        pollvoteadderror: 'Ein Fehler ist beim Hinzufügen deiner Stimme aufgetreten. Bitte versuche es erneut.',
        pollvoteremoved: 'Deine Stimme wurde entfernt',
        pollvoteremoveerror: 'Ein Fehler ist beim Entfernen deiner Stimme aufgetreten. Bitte versuche es erneut.',
        question: 'Frage',
        questionPlaceholder: 'Frage eingeben',
        voters: 'Abstimmende',
        votes: 'Stimmen',
    },
    social: {
        facebook: 'Facebook',
        instagram: 'Instagram',
        linkedin: 'LinkedIn',
        reddit: 'Reddit',
        twitter: 'Twitter/X',
        youtube: 'YouTube',
    },
    units: {
        days: 'Tag | Tage',
        followers: 'Follower | Follower',
        hours: 'Stunde | Stunden',
        months: 'Monat | Monate',
        weeks: 'Woche | Wochen',
        years: 'Jahr | Jahre',
    },
    userstatuslabels: {
        0: 'Aktiv',
        1: 'Austehend',
        2: 'Gelöscht',
        3: 'Gesperrt',
        4: 'Gekündigt',
        5: 'Wartet auf Genehmigung',
        6: 'Abgelehnt',
        7: 'Gast',
    },
};
export default deDE;
